import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { FaExclamationTriangle, FaCheck, FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaExpand, FaCompress, FaPlay } from 'react-icons/fa';
import { getBlockers } from '../../../utils/idHelpers';
import {
    getStatusColour,
    PARENT_COLOUR,
    CLIENT_PARENT_COLOUR,
    STATUS_COLOUR,
    PROJECT_COLOUR,
    KICKOFF_COLOUR,
    getBarGradient,
    getNumericRef,
    getColumnMetrics,
    calculateStatusProgress,
    getProgressWash,
    anchoredScaleScroll,
    dragTypeFor,
    snapUiScale,
    UI_SCALE_DEFAULT,
    TOUCH_TAP_SLOP,
    ganttTextVisible,
    fitUiScale,
    percentLabelFits,
    barNameOverflows,
    statusLabel,
    BLOCKED_ICON_WIDTH,
    AFTER_BAR_NAME_CLASSES,
    AFTER_BAR_NAME_GLOW,
    AFTER_BAR_NAME_OFFSET
} from './gantt/GanttUtils';
import { useGanttState } from './gantt/GanttState';
import { useAnchoredPlacement } from './gantt/useAnchoredPlacement';
import { useGanttPan } from './gantt/GanttPan';
import { useGanttGestures } from './gantt/GanttGestures';
import { toDayNum, fromDayNum, clampMove, cascadeResize } from '../../../utils/fsConstraints';
import { confirmProjectWindow } from '../../../utils/projectWindow';
import { confirmClampAdjustments, alertEndBeforeStart } from '../../../utils/clampFeedback';
import { StatusChangePopup, AssigneeChangePopup, DependencyColourPicker, CustomTooltip } from './gantt/GanttPopups';
import DependencyArrows from './gantt/DependencyArrows';
import GanttDragConfirm from './gantt/GanttDragConfirm';
import ZoomSlider from './gantt/ZoomSlider';
import { makeTextMeasurer, flowWords } from './gantt/nameFlow';
import { applyViewMode, expansionFor } from './gantt/viewModes';
import DateSlider from './gantt/DateSlider';
import { buildGanttRows } from './gantt/GanttRowBuilder';
import Tooltip, { BUBBLE_CLASSES } from '../../../components/Tooltip';
import { effortLabel, isOverrun, isClientLaneTask } from '../../../utils/effortDisplay';

// Collapsible-column chevrons (organise T162 tidy-up style)
const COL_CHEVRON_CLASS = 'flex-shrink-0 text-lg leading-4 text-gray-700 hover:text-gray-900';

// How long Follow mode takes to travel, whatever the distance (Bill, 2026-08-23:
// "500ms is too small, go 1.5 seconds"). FIXED on purpose: the browser's own
// `behavior: 'smooth'` scales its duration with distance, and a Gantt jump is
// tens of thousands of pixels - on preview it crawled for many seconds.
const FOLLOW_SCROLL_MS = 1500;

// Every chart row is this tall AT A READING SIZE OF 1. At module scope so Follow
// mode's scroll maths can reach it - the anchor effect sits well above where
// this used to be declared, and row i's top is simply i * rowHeight under the
// sticky header.
//
// ⛔ EVERY CONSUMER USES THE SCALED VALUE, NOT THIS ONE (T372, 2026-08-30).
// uiScale makes the rows really taller - it is not a CSS transform - so the
// arithmetic that positions rows, the today band, the arrows and Follow mode's
// anchor all have to agree with what was drawn. `scaledRowHeight` inside the
// component is that value; this constant is only its base.
const BASE_ROW_HEIGHT = 36;

// What the chart looked like before Follow mode took it over, so exiting can
// put it back (D12, D22). Held in localStorage rather than a ref because
// Follow mode itself survives a reload.
//
// VALIDATED ON READ, and the whole key is rejected on anything unexpected.
// localStorage is user-editable and parseable garbage is the dangerous case:
// getColumnMetrics is a bare ternary chain, so a scale of "potato" silently
// takes the MONTH branch and the chart comes back at the wrong geometry with
// nothing logged. A half-restored view is harder to spot than one that plainly
// did not restore.
const readFollowPrev = () => {
    try {
        const v = JSON.parse(localStorage.getItem('ganttFollowPrev'));
        if (!v || typeof v.hideCompleted !== 'boolean') return null;
        if (!['day', 'week', 'month'].includes(v.scale)) return null;
        if (!['all', 'tme', 'team'].includes(v.laneFilter)) return null;
        if (v.viewMode !== null && !['mt', 'mtst', 'st'].includes(v.viewMode)) return null;
        if (v.expandedItems === null || typeof v.expandedItems !== 'object' || Array.isArray(v.expandedItems)) return null;
        if (!Number.isFinite(v.scrollDays) || !Number.isFinite(v.scrollTop)) return null;
        return v;
    } catch { return null; }
};

// Get today (user's local calendar day) as UTC midnight, consistent with
// parsed YYYY-MM-DD task dates
const utcToday = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

// Format date as YYYY-MM-DD. This is the DATA format - API payloads, day-number
// keys and <input type="date"> values all require it. Never use it for text the
// user reads; use formatDateUK for that.
const formatDateYMD = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
};

// UK display format (DD/MM/YYYY). UTC to match the rest of the chart, which
// treats dates as calendar days with no timezone shift.
const formatDateUK = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', { timeZone: 'UTC' });
};

// Calculate duration in working days (excludes weekends)
const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);

    let workingDays = 0;
    const current = new Date(startDate);

    while (current <= endDate) {
        const dayOfWeek = current.getUTCDay();
        // 0 = Sunday, 6 = Saturday
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            workingDays++;
        }
        current.setUTCDate(current.getUTCDate() + 1);
    }

    return workingDays > 0 ? workingDays : '';
};


// ISO 8601 week number. Week 1 is the week containing the first Thursday of the
// year, so a week is numbered by the year its Thursday falls in - which is why
// this works off the Thursday of the week rather than the date handed in.
//
// The previous version counted days from 1 January and added the year's starting
// weekday, which double-counted the offset: in a year starting late in the week
// (2027 begins on a Friday) the first Monday scored 2, so W1 could never appear
// and the header jumped W53 -> W2.
const getWeekNumber = (date) => {
    const d = new Date(date);
    // Thursday of this week (Monday-based: Mon=0 ... Sun=6)
    const thursday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    thursday.setUTCDate(thursday.getUTCDate() - ((thursday.getUTCDay() + 6) % 7) + 3);
    // Thursday of week 1 of the year that Thursday belongs to
    const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
    firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
    return 1 + Math.round((thursday - firstThursday) / (7 * 24 * 60 * 60 * 1000));
};

// Get date string for a click position on a bar
const getClickedDate = (clickX, barLeft, barWidth, startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.max((end - start) / (1000 * 60 * 60 * 24) + 1, 1);
    const clickOffset = clickX - barLeft;
    const dayOffset = Math.floor((clickOffset / barWidth) * totalDays);
    const clickedDate = new Date(start);
    clickedDate.setUTCDate(clickedDate.getUTCDate() + dayOffset);
    return clickedDate;
};

// Check if a date is a weekend (Saturday or Sunday)
const isWeekend = (date) => {
    const day = date.getUTCDay();
    return day === 0 || day === 6;
};

// Get the last working day before a given date
const getLastWorkingDayBefore = (date) => {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() - 1); // Go back one day
    while (isWeekend(d)) {
        d.setUTCDate(d.getUTCDate() - 1);
    }
    return d;
};

// Calculate bar segments for a subtask, splitting at non-worked weekends
// Returns array of { left, width, isWeekend, isWorked, dateKey }
const calculateBarSegments = (row, pixelsPerDay, minDate, weekendWorkData) => {
    if (!row.start || !row.end) return [];

    const start = new Date(row.start);
    const end = new Date(row.end);
    // Use string key for consistency with storage
    const subtaskWork = weekendWorkData[String(row.originalId)] || {};
    const segments = [];
    let currentDate = new Date(start);
    let segmentStart = null;
    let segmentIsWeekend = false;

    while (currentDate <= end) {
        const dateKey = formatDateYMD(currentDate);
        const dayIsWeekend = isWeekend(currentDate);
        const isWorked = dayIsWeekend ? (subtaskWork[dateKey] === true) : true;

        if (segmentStart === null) {
            // Start a new segment
            segmentStart = new Date(currentDate);
            segmentIsWeekend = dayIsWeekend && !isWorked;
        } else {
            const currentShouldBeWeekendGap = dayIsWeekend && !isWorked;
            if (currentShouldBeWeekendGap !== segmentIsWeekend) {
                // End current segment and start new one
                const segmentDays = (currentDate - segmentStart) / (1000 * 60 * 60 * 24);
                const startOffset = (segmentStart - minDate) / (1000 * 60 * 60 * 24);
                segments.push({
                    left: startOffset * pixelsPerDay,
                    width: segmentDays * pixelsPerDay,
                    isWeekendGap: segmentIsWeekend
                });
                segmentStart = new Date(currentDate);
                segmentIsWeekend = currentShouldBeWeekendGap;
            }
        }
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Add final segment
    if (segmentStart !== null) {
        const segmentDays = Math.max((end - segmentStart) / (1000 * 60 * 60 * 24) + 1, 1);
        if (segmentDays > 0) {
            const startOffset = (segmentStart - minDate) / (1000 * 60 * 60 * 24);
            segments.push({
                left: startOffset * pixelsPerDay,
                width: segmentDays * pixelsPerDay,
                isWeekendGap: segmentIsWeekend
            });
        }
    }

    return segments;
};

const GanttChart = ({
    tasks = [],
    links = [],
    subtasks = [],
    statuses = [],
    epics = [],
    onTaskClick,
    readOnly = true,
    onTaskUpdate,
    onParentTaskMove,
    onDependencyCreate,
    onDependencyDelete,
    onDependencyColorChange,
    onSubtaskOrderChange,
    onMainTaskOrderChange,
    teamSwimlaneId,
    taskStatuses = [],
    onStatusChange,
    weekendWork = {},
    onWeekendWorkChange,
    kickOffDate,
    onKickOffDateChange,
    onProjectDateShift,
    shiftProgress,
    blockedTasksMap = new Map(),
    members = [],
    onAssigneeChange,
    onClosureRequired,
    // Full-screen mode (Bill, 2026-08-21): the PAGE owns the state - it is the
    // page's chrome that hides - so the chart just shows the toggle. The button
    // renders only when a handler is passed; the template editor passes none.
    fullScreen = false,
    onToggleFullScreen,
    // ⛔ EFFORT IS ADMIN-ONLY (D4) AND THIS DEFAULTS TO FALSE. This component
    // renders in the CLIENT portal's Timeline as well as the admin Client View,
    // and `readOnly` is NOT a proxy for "who is looking" - it says whether this
    // view can edit. The server strips effort from every client read (T466);
    // this is the second gate, and it fails closed. Used by T475's column and
    // T471's bar label.
    showEffort = false,
}) => {
    // Shared Gantt UI state from custom hook
    const {
        scrollContainerRef, containerRef, headerRef,
        containerWidth, headerHeight,
        currentScale, setCurrentScale,
        zoomLevel, setZoomLevel, hasZoomForScale, hasAnyZoom,
        uiScale, setUiScale, persistUiScale,
        persistZoom, gestureActiveRef,
        expandedItems, setExpandedItems,
        reorderDrag, setReorderDrag,
        dragState, setDragState,
        dragPreview, setDragPreview,
        pushedTaskPreview, setPushedTaskPreview,
        dragStateRef, dragPreviewRef, pushedTaskPreviewRef,
        positionOverridesRef,
        pendingLinks, setPendingLinks,
        editingDateRow, setEditingDateRow,
        dependencyDrag, setDependencyDrag,
        cursorPos, setCursorPos,
        dependencyDragRef,
        statusPopup, setStatusPopup,
        assigneePopup, setAssigneePopup,
        weekendPopup, setWeekendPopup,
        colorPopup, setColorPopup,
        topLinkKey, setTopLinkKey,
        tooltip, setTooltip,
        nameColWidth, setNameColWidth,
        colResizing, setColResizing,
        nameColCollapsed, setNameColCollapsed,
        datesCollapsed, setDatesCollapsed,
        viewMode, setViewMode,
        laneFilter, setLaneFilter,
        scrollPercent, setScrollPercent,
        effectiveLinks
    } = useGanttState('gantt', links, subtasks, tasks);

    // Component-specific refs (not shared via hook)
    const didDragRef = useRef(false);
    const pixelsPerDayRef = useRef(0);
    const tooltipTimeoutRef = useRef(null);

    // ⛔ ONE COPY OF THE CLAMP-AND-FLIP, SHARED WITH THE OTHER CHART AND WITH THE
    // DRAG CONFIRMATION (T403). This was an identical 25-line effect in both
    // Gantts and was about to be a third copy in the confirmation; the flip is
    // the case that would have drifted, and it is the one that matters.
    //
    // ⚠️ The clearer is what CustomTooltip's own onMouseLeave calls. The
    // placement already dies with its anchor, so this is belt and braces - but
    // the prop is not optional and a no-op passed in its place would be a lie.
    const { ref: tooltipRef, placed: tooltipPos, clear: clearTooltipPos } =
        useAnchoredPlacement(tooltip);

    // Hide completed main tasks (persisted, defaults to showing them)
    const [hideCompleted, setHideCompleted] = useState(() => {
        return localStorage.getItem('ganttHideCompleted') === 'true';
    });

    useEffect(() => {
        localStorage.setItem('ganttHideCompleted', hideCompleted.toString());
    }, [hideCompleted]);

    // Follow mode (Bill, 2026-08-23): the office-TV mode. Full screen, hide
    // completed, Day scale, and the chart walks itself right and down as tasks
    // close. Persisted like every other Gantt setting, and shared with the
    // other portal for the same reason the scale and zoom keys are.
    const [followMode, setFollowMode] = useState(
        () => localStorage.getItem('ganttFollowMode') === 'true'
    );

    useEffect(() => {
        localStorage.setItem('ganttFollowMode', followMode.toString());
    }, [followMode]);

    // Drag the chart about by its free space. Off while Follow mode runs (Bill,
    // 2026-08-24): Follow re-snaps 150ms after any scroll it did not cause
    // (:2101), so a pan there would slide back on its own and read as broken.
    // isPanningRef feeds T352's hover-card guards.
    // Every touch pointer currently down on the chart. ⛔ THE CHART OWNS ITS
    // IDENTITY and useGanttGestures is its only WRITER - the pan, the bar drag
    // and the column resize all read it, and a second copy would answer a
    // different question from the one that armed the gesture.
    const pointersRef = useRef(new Map());

    // Whether a pinch may ARM. Declared here rather than beside the gesture
    // wiring below because it is assigned as soon as anyDragActive exists, which
    // is a long way above that.
    const gestureBlockedRef = useRef(false);

    // ⛔ THE ROW HEIGHT EVERY CONSUMER MUST USE. The rows really are taller at a
    // larger reading size - nothing is magnified - so the row positions, the
    // today band's height, the dependency arrows' row maths and Follow mode's
    // anchor all have to agree with what was drawn. Keeping the name `rowHeight`
    // is deliberate: every existing consumer picks up the scaled value without
    // being touched, and there is no second variable to forget.
    //
    // ⚠️ ROUNDED, so row tops stay on whole pixels. A fractional row height puts
    // every arrow and the today band a fraction off, which reads as blurring.
    const rowHeight = Math.round(BASE_ROW_HEIGHT * uiScale);

    // Below the reading-size floor for text, every label comes off - see
    // UI_SCALE_TEXT_MIN. One boolean, read by the bar labels and the left panel,
    // so the two can never disagree about what "too small to read" means.
    const showText = ganttTextVisible(uiScale);

    const { isPanningRef } = useGanttPan(scrollContainerRef, {
        disabled: followMode,
        pointersRef
    });

    // Until when a scroll event should be read as Follow mode's own doing.
    // Stamped before every programmatic scroll; anything arriving after it is
    // the user, and the user gets snapped back (D9). See the scroll listener.
    const autoScrollUntilRef = useRef(0);

    // The in-flight follow scroll, so a new anchor can take over mid-travel
    // instead of two tweens fighting for the same scrollLeft.
    const followTweenRef = useRef(null);

    // The view to put back when follow mode ends - see exitFollow.
    const pendingFollowRestoreRef = useRef(null);

    // Set only by the AUTO-FIT effect, and only while following, so the layout
    // effect can tell that one deferred scroll apart from every other. It must:
    // auto-fit's write has to lose to the anchor, while a manual zoom's write
    // has to win (D10 - a zoom does not re-anchor) and a manual FIT's has to
    // land and then lose to the 150ms snap-back. Same mechanism, three
    // different right answers, so the origin is marked rather than guessed.
    const autoFitPendingRef = useRef(false);

    // Transient "that control is locked" message - hide completed and full
    // screen both refuse while following (D7, D23).
    const [lockedMsg, setLockedMsg] = useState(null);

    useEffect(() => {
        if (!lockedMsg) return undefined;
        const timer = setTimeout(() => setLockedMsg(null), 4000);
        return () => clearTimeout(timer);
    }, [lockedMsg]);

    // ⛔ AN EDITOR CANNOT SURVIVE ITS OWN CELL BEING HIDDEN (D14, T398). The
    // Start cell's date input commits on BLUR, and an unmount fires no blur - so
    // without this the typed value is dropped AND editingDateRow stays set,
    // re-opening the editor on the way back up. Clearing it discards the
    // in-flight edit deliberately (Bill, 2026-08-30): it takes an accidental
    // trackpad pinch with an editor open and no click in between, and a
    // half-committed date is worse than a discarded one.
    useEffect(() => {
        if (!showText && editingDateRow !== null) setEditingDateRow(null);
    }, [showText, editingDateRow, setEditingDateRow]);

    // Date navigation: which end of the scroll the chart is sitting at, so the
    // step buttons pointing that way can be disabled. Deliberately two booleans
    // rather than the raw offset - see the scroll listener below.
    const [navEdges, setNavEdges] = useState({ atStart: true, atEnd: false });

    // Percent of the scroll range that ONE DAY represents - the footer slider's
    // step. 100 means "nothing to scroll", so the slider sits at one end.
    const [sliderStep, setSliderStep] = useState(100);

    // Shown when Today is pressed on a project whose tasks do not span today.
    const [todayOutsideRange, setTodayOutsideRange] = useState(false);

    // Measures bar-name text for the weekend-gap word flow. document.body
    // because no element overrides the family and the container ref is null on
    // first render.
    //
    // 600 18px - the weight AND size the label renders at. It ran a deliberate
    // 1px behind (17px, Bill 2026-08-23) so the flow packed slightly tight, but
    // the overpacked text painted over the › chevron's air on weekend-gapped
    // bars with long names, so the two were put back in step (Bill, 2026-08-30).
    //
    // ⛔ AND IT MOVES WITH uiScale (T372, 2026-08-30). The label renders at
    // calc(18px * var(--gantt-scale)), so a fixed-size measurer under-measures
    // every name by the scale factor - the word flow packs in far too much, the
    // › continuation chevrons land in the wrong places and the text overflows
    // the bars. SILENTLY: no test turns red, because jsdom measures nothing.
    // uiScale is in the dependency array for the same reason.
    const nameMeasure = useMemo(
        () => makeTextMeasurer(`600 ${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    // The percentage label's own measurer (T397). Deliberately NOT nameMeasure:
    // that one is semibold and the percentage span carries no font-weight
    // class, and here erring tight would SHOW a label that does not fit, which
    // is the defect being removed.
    const percentMeasure = useMemo(
        () => makeTextMeasurer(`${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    // The bar name's OWN measurer (T392) - 600 weight, the label's real 18px.
    // Same font string as nameMeasure since 2026-08-30, but kept separate: not
    // percentMeasure (18px but plain weight), and three labels, three
    // measurers, each matching what it measures. Getting this wrong is silent -
    // jsdom measures nothing.
    const barNameMeasure = useMemo(
        () => makeTextMeasurer(`600 ${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    // Follow mode persists, so a chart mounting with it on asks the page for
    // full screen itself - otherwise the TV comes back from a reboot following
    // the project in a small embedded chart.
    useEffect(() => {
        if (followMode && onToggleFullScreen) onToggleFullScreen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // T402. The question a finger's bar drag asks before anything is written.
    // Holds the fully-planned commit (§6.2) plus the anchor; null when nothing
    // is being asked.
    //
    // ⛔ Component-local, NOT in useGanttState: it must not survive a remount,
    // and nothing outside this chart reads it. ⚠️ Declared HERE, well above the
    // release path that sets it, because the full-screen Escape effect below
    // reads it - a const declared after that effect is in its temporal dead
    // zone and the chart throws on first render.
    const [dragConfirm, setDragConfirm] = useState(null);

    // Esc leaves full screen. Listener exists only while in it.
    //
    // Inert while following (D11): the play button is the only way out, and it
    // drops full screen with it. followMode is in the dependency array for a
    // reason - without it the effect keeps a stale value and Esc stays dead
    // after leaving follow, a guard outliving the state it guards.
    //
    // ⛔ AND NOT WHILE A DRAG CONFIRMATION IS OPEN (T402). Both listeners are on
    // `window`, so one Escape would cancel the question AND drop out of full
    // screen - the user answering "put it back" and losing the chart with it.
    // Gated here rather than by stopPropagation in the popup, because the popup
    // listens on `document` and the ordering of two listeners on two different
    // targets is not something a test should have to encode.
    useEffect(() => {
        if (!fullScreen || !onToggleFullScreen || followMode || dragConfirm) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onToggleFullScreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullScreen, onToggleFullScreen, followMode, dragConfirm]);

    // Hover labels for the navigation buttons. The native title attribute is
    // both ugly and slow (~1s), so these use the same black card as the
    // dependency-line tip, shown with no delay.
    const [navTip, setNavTip] = useState(null);
    const navTipTimeoutRef = useRef(null);
    const showNavTip = (e, text) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const tip = { text, x: rect.left + rect.width / 2, y: rect.bottom + 8 };
        if (navTipTimeoutRef.current) clearTimeout(navTipTimeoutRef.current);
        navTipTimeoutRef.current = setTimeout(() => setNavTip(tip), 400);
    };
    const hideNavTip = () => {
        if (navTipTimeoutRef.current) clearTimeout(navTipTimeoutRef.current);
        setNavTip(null);
    };


    // Kick-off marker drag state (calendar-only)
    const [kickOffDragging, setKickOffDragging] = useState(false);
    const [kickOffPreviewPosition, setKickOffPreviewPosition] = useState(null);

    // Every drag hides both hover cards (Bill, 2026-08-24). The bar tooltip is a
    // 700ms timer, so it lands when you pause mid-drag without letting go. The
    // dependency-line card is INSTANT and drags the chain dimming with it, so
    // merely crossing an arrow flashes every unrelated bar to 25%.
    //
    // Both halves below are needed. The guards stop a new card arming while a
    // drag runs; the effect kills one that is ALREADY showing, which a bar drag
    // always has - it starts from a hover, so the card is up before the button
    // goes down and no guard would ever remove it.
    //
    // Not `isDragging`: that name is a per-row local at :2783.
    const anyDragActive = !!(
        dragState || dependencyDrag || reorderDrag || colResizing || kickOffDragging
    );

    // ⛔ The pinch may not ARM while a drag is live - but the pointer map is
    // still written, because the drags' aborts are exactly what read it.
    gestureBlockedRef.current = anyDragActive;

    useEffect(() => {
        if (!anyDragActive) return;
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        setTooltip(null);
        setLinkTip(null);
        // The three setters are stable and listing them says nothing; what this
        // effect watches is the drag STARTING, and only that.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyDragActive]);

    // T409/D12. What a FINGER gets from the kick-off marker: its label, and no
    // movement. Holds the bubble's viewport position, or null.
    const [kickOffTapLabel, setKickOffTapLabel] = useState(null);
    const kickOffTapTimerRef = useRef(null);

    // Project date shift confirmation dialog state (calendar-only)
    const [shiftConfirmation, setShiftConfirmation] = useState(null);

    // Footer date picker selection (ISO - the native input requires it)
    const [jumpDate, setJumpDate] = useState('');

    // Footer slider, mid-drag. Imperative ONLY - deliberately no setState.
    //
    // scrollPercent is read in exactly one place (the slider's own value), so
    // updating it here repainted every bar and every dependency arrow for a
    // value the chart never consumes. That was the jerk: the native horizontal
    // scrollbar was smooth because it scrolls on the compositor with no React
    // involved, and this is now the same amount of work.
    const handleSliderScrub = (percent) => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        scrollContainer.scrollLeft = (percent / 100) * maxScroll;
    };

    // Footer slider, on release. One re-render per drag instead of one per step.
    const handleSliderCommit = (percent) => setScrollPercent(percent);

    // Date at the LEFT edge of the window for a given slider position - the
    // label the footer slider shows while it is being dragged. Same px-per-day
    // maths the Fit calculation uses (baseW/dPerCol by scale, times zoom).
    const dateAtScrollPercent = (percent) => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || !dateRange?.minDate) return '';
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        if (maxScroll <= 0) return formatDateUK(dateRange.minDate);
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const days = Math.floor(((percent / 100) * maxScroll) / pxPerDay);
        const edge = new Date(dateRange.minDate);
        edge.setDate(edge.getDate() + days);
        return formatDateUK(edge);
    };

    // The warning is transient - it explains a jump that has already happened.
    useEffect(() => {
        if (!todayOutsideRange) return undefined;
        const timer = setTimeout(() => setTodayOutsideRange(false), 4000);
        return () => clearTimeout(timer);
    }, [todayOutsideRange]);

    // Step the timeline by whole days. Instant, never animated: smooth scrolling
    // reads a mid-animation offset on the next click, so repeated presses land
    // short of where they should.
    const scrollByDays = (days) => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        el.scrollLeft = Math.max(0, Math.min(maxScroll, el.scrollLeft + days * pxPerDay));
    };

    // Centre today in the visible timeline, and warn if the project's own tasks
    // do not span it. The chart still moves - refusing to would leave the same
    // view on screen with a message and no way to see where today falls.
    const handleToday = () => {
        const el = scrollContainerRef.current;
        if (!el || !dateRange?.minDate) return;
        const today = utcToday();

        // The project row carries the task span with today excluded, which is
        // what "in project range" means here. The rendered range always contains
        // today (calculateDateRange pushes it in), so it cannot answer this.
        const projectRow = rows.find(r => r.id === 'project');
        if (projectRow?.start && projectRow?.end) {
            const spanStart = new Date(projectRow.start);
            const spanEnd = new Date(projectRow.end);
            setTodayOutsideRange(today < spanStart || today > spanEnd);
        }

        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const timelineWidth = Math.max(0, el.clientWidth - leftPanelWidth);
        const todayX = ((today - dateRange.minDate) / 86400000) * pxPerDay;
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        el.scrollLeft = Math.max(0, Math.min(maxScroll, todayX - timelineWidth / 2));
    };

    // Handle Fit button - calculate zoom to fit visible rows and set it
    const handleFit = () => {
        // Fit = zoom so the subtasks currently on screen fill the timeline
        // width exactly (Bill's spec, 2026-07-29). No step rounding, no flat
        // 14-day padding - a 1-day margin each side, clamped 25%-800%.
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || rows.length === 0) return;

        // Fit rule (Bill, 2026-07-29): off-screen RIGHT and LEFT ignored,
        // off-screen ABOVE and BELOW included. Every subtask in the chart
        // whose bar overlaps the current date window counts, whatever row
        // it is in; partially visible bars are fitted IN FULL. Nothing in
        // the window = Fit does nothing.
        const rowWindow = rows.filter(r => r.type === 'subtask' && (r.start || r.end));

        const { pxPerDay: curPxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const winStartDays = scrollContainer.scrollLeft / curPxPerDay;
        const winEndDays = (scrollContainer.scrollLeft + scrollContainer.clientWidth) / curPxPerDay;

        const candidates = rowWindow.filter(r => {
            const sDays = (new Date(r.start || r.end) - dateRange.minDate) / 86400000;
            const eDays = (new Date(r.end || r.start) - dateRange.minDate) / 86400000 + 1;
            return eDays >= winStartDays && sDays <= winEndDays;
        });
        if (candidates.length === 0) return;

        let minDate = null;
        let maxDate = null;
        for (const row of candidates) {
            if (row.start) {
                const d = new Date(row.start);
                if (!minDate || d < minDate) minDate = d;
            }
            if (row.end) {
                const d = new Date(row.end);
                if (!maxDate || d > maxDate) maxDate = d;
            }
        }
        if (!minDate || !maxDate) {
            minDate = dateRange.minDate;
            maxDate = dateRange.maxDate;
        }
        if (!minDate || !maxDate) return;

        // Inclusive span plus the 1-day margin each side
        const spanDays = (maxDate - minDate) / 86400000 + 1 + 2;

        const { baseWidth, daysPerCol } = getColumnMetrics(currentScale);
        const viewW = scrollContainer.clientWidth - leftPanelWidth;
        if (viewW <= 0 || spanDays <= 0) return;

        // Exact zoom that makes the span fill the visible timeline width
        const fitZoom = Math.max(0.25, Math.min(8, (viewW * daysPerCol) / (spanDays * baseWidth)));

        // Deterministic scroll via the zoom-anchoring mechanism: span start
        // (minus the margin day) lands at the left edge
        const { pxPerDay: newPxPerDay } = getColumnMetrics(currentScale, fitZoom);
        const offsetDays = (minDate - dateRange.minDate) / 86400000 - 1;
        const targetScroll = Math.max(0, offsetDays * newPxPerDay);
        if (Math.abs(fitZoom - (zoomLevel || 1)) < 0.001) {
            // Already at the fit zoom: a same-value setZoomLevel is a render
            // no-op and the layout effect never fires, so scroll directly - but
            // still write it, because the per-scale map uses presence to mean
            // "this scale has been fitted".
            setZoomLevel(fitZoom);
            scrollContainer.scrollLeft = targetScroll;
        } else {
            pendingScrollRef.current = targetScroll;
            setZoomLevel(fitZoom);
        }
    };

    // All = the ENTIRE project's subtasks zoomed to the closest fit.
    //
    // ⛔ THE HORIZONTAL HALF ALONE, AND THE AUTO-FIT EFFECT CALLS THIS ONE
    // (T386, 2026-08-30). ALL fits both axes now, but the auto-fit effect below
    // is not a button press: it runs on first load and on the first visit to
    // each scale. Point it at the two-axis handleFitAll and every first load
    // silently overwrites the reading size the user chose and persisted - read
    // from localStorage, applied, then thrown away 100ms later by a fit nobody
    // asked for.
    const fitAllHorizontal = () => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || rows.length === 0) return;

        let candidates = rows.filter(r => r.type === 'subtask' && (r.start || r.end));
        if (candidates.length === 0) candidates = rows.filter(r => r.start || r.end);
        if (candidates.length === 0) return;

        let minDate = null;
        let maxDate = null;
        for (const row of candidates) {
            if (row.start) {
                const d = new Date(row.start);
                if (!minDate || d < minDate) minDate = d;
            }
            if (row.end) {
                const d = new Date(row.end);
                if (!maxDate || d > maxDate) maxDate = d;
            }
        }
        if (!minDate || !maxDate) return;

        const spanDays = (maxDate - minDate) / 86400000 + 1 + 2;
        const { baseWidth, daysPerCol } = getColumnMetrics(currentScale);
        const viewW = scrollContainer.clientWidth - leftPanelWidth;
        if (viewW <= 0 || spanDays <= 0) return;

        const fitZoom = Math.max(0.25, Math.min(8, (viewW * daysPerCol) / (spanDays * baseWidth)));
        const { pxPerDay: newPxPerDay } = getColumnMetrics(currentScale, fitZoom);
        const offsetDays = (minDate - dateRange.minDate) / 86400000 - 1;
        const targetScroll = Math.max(0, offsetDays * newPxPerDay);
        if (Math.abs(fitZoom - (zoomLevel || 1)) < 0.001) {
            // Same value, but it must still be WRITTEN: the per-scale map uses
            // presence to mean "this scale has been fitted". Skipping the write
            // left the scale marked unvisited for ever, stuck at zoom 1.
            setZoomLevel(fitZoom);
            scrollContainer.scrollLeft = targetScroll;
        } else {
            pendingScrollRef.current = targetScroll;
            setZoomLevel(fitZoom);
        }
    };

    // The vertical half. Rows as RENDERED (Bill, 2026-08-30): collapse,
    // hide-completed and the lane filter are all respected, and nothing is
    // expanded to make the fit "complete".
    const fitAllVertical = () => {
        const el = scrollContainerRef.current;
        if (!el || rows.length === 0) return;
        // The same header-height expression the rest of this file already uses
        // for its pinned rows - the file's existing idiom rather than a new
        // fallback. headerHeight is a ResizeObserver measurement of the header's
        // offsetHeight and is 0 for the first frames.
        const header = headerHeight || (currentScale !== 'month' ? 64 : 36);
        const next = fitUiScale(el.clientHeight - header, rows.length, BASE_ROW_HEIGHT);
        if (next === null) return;

        // ⛔ STAMPED BEFORE THE BRANCH, NOT INSIDE IT. Both branches below write
        // scroll, and both are Follow mode allowing a SCALE change (D10 - the
        // re-anchor effect is not keyed on it) while still owning the scroll
        // position. Stamp after the early return and ALL pressed while following
        // at an already-fitted reading size writes a scroll Follow reads as the
        // user's, and fights.
        if (followMode) autoScrollUntilRef.current = performance.now() + 800;

        if (next === uiScale) {
            // Already at the fit: no re-render is coming, so the layout effect
            // will not run and the scroll has to be written here - the same
            // shape as the horizontal half's same-zoom branch above.
            el.scrollTop = 0;
            return;
        }

        // ⛔ DEFERRED, like every other reading-size change. Rows are only their
        // new height AFTER the render, so scrollHeight is wrong until then.
        pendingScrollTopRef.current = 0;
        setUiScale(next);
        // A click, not a continuous gesture: durable at once, and with an
        // EXPLICIT value because uiScaleRef still holds the pre-click one this
        // tick.
        persistUiScale(next);
    };

    // The BUTTON fits both axes (Bill, 2026-08-30: "Yes - ALL fits both axes").
    const handleFitAll = () => {
        fitAllHorizontal();
        fitAllVertical();
    };

    // The project's own span, measured across EVERY task - closed, hidden and
    // collapsed included.
    //
    // The Project Overview bar used to take its dates from the rows currently
    // on screen, so closing a task with hide-completed on removed that task's
    // dates from the pool and the bar jumped forward. The project's extent does
    // not change because of what is being displayed.
    const projectBounds = useMemo(() => {
        let min = null;
        let max = null;

        const consider = (item) => {
            // Same precedence as getEffectiveDates below
            const rawStart = item.startDate || item.start || item.dueDate;
            const rawEnd = item.dueDate || item.end;
            if (rawStart) {
                const d = new Date(rawStart);
                if (!isNaN(d) && (!min || d < min)) min = d;
            }
            if (rawEnd) {
                const d = new Date(rawEnd);
                if (!isNaN(d) && (!max || d > max)) max = d;
            }
        };

        (tasks || []).forEach(consider);
        (subtasks || []).forEach(consider);

        return { min, max };
    }, [tasks, subtasks]);

    // Calendar-specific date helpers for row building
    const calendarDateHelpers = {
        taskStatuses,
        getEffectiveDates: (item) => ({
            start: item.startDate || item.start || item.dueDate,
            end: item.dueDate || item.end
        }),
        calculateSummaryDates: (children) => {
            let minStart = null;
            let maxEnd = null;
            let totalProgress = 0;
            let progressCount = 0;

            for (const child of children) {
                if (child.start) {
                    const startDate = new Date(child.start);
                    if (!minStart || startDate < minStart) minStart = startDate;
                }
                if (child.end) {
                    const endDate = new Date(child.end);
                    if (!maxEnd || endDate > maxEnd) maxEnd = endDate;
                }
                if (typeof child.progress === 'number') {
                    totalProgress += child.progress;
                    progressCount++;
                }
            }

            return {
                start: minStart ? formatDateYMD(minStart) : null,
                end: maxEnd ? formatDateYMD(maxEnd) : null,
                progress: progressCount > 0 ? Math.round(totalProgress / progressCount) : 0
            };
        },
        calculateDateRange: (rowList) => {
            const today = utcToday();

            const allDates = rowList.flatMap(r => [
                r.start ? new Date(r.start) : null,
                r.end ? new Date(r.end) : null
            ]).filter(Boolean);

            allDates.push(today);

            let minDate = new Date(Math.min(...allDates));
            let maxDate = new Date(Math.max(...allDates));

            // Project summary. Deliberately NOT derived from rowList: these
            // must not move when a task is closed, hidden or collapsed.
            //
            // Start is the kick-off - the project's real beginning, and the
            // same date the kick-off marker uses. Falls back to the earliest
            // task when no kick-off is set. End has no stored equivalent, so it
            // stays derived, but from every task rather than the visible ones.
            const projectStart = kickOffDate
                ? new Date(kickOffDate)
                : (projectBounds.min || today);
            const projectEnd = projectBounds.max || projectStart;

            // Overall progress across EVERY subtask, for the same reason the
            // dates above are: rowList holds only what is on screen, so
            // averaging it made project progress FALL when hide-completed was
            // switched on - the finished subtasks were the ones being removed.
            const allSubtasks = subtasks || [];
            const projectProgress = allSubtasks.length > 0
                ? Math.round(
                    allSubtasks.reduce(
                        (sum, st) => sum + calculateStatusProgress(st, taskStatuses),
                        0
                    ) / allSubtasks.length
                )
                : 0;

            // Add Project row at the top
            const projectRow = {
                id: 'project',
                originalId: 'project',
                type: 'project',
                text: 'Project Overview',
                start: formatDateYMD(projectStart),
                end: formatDateYMD(projectEnd),
                progress: projectProgress,
                level: 0,
                hasChildren: false,
                isExpanded: false,
                isSummary: true,
                isClosed: false
            };

            // Insert project row at the beginning
            rowList.unshift(projectRow);

            // The window is sized from the visible rows, but it must still
            // contain the Project Overview bar - otherwise a kick-off earlier
            // than the first visible task renders off the left edge.
            if (projectStart < minDate) minDate = new Date(projectStart);
            if (projectEnd > maxDate) maxDate = new Date(projectEnd);

            // Add padding - 1 month before, at least 1 year into the future
            minDate.setUTCMonth(minDate.getUTCMonth() - 1);
            const oneYearFromNow = new Date(today);
            oneYearFromNow.setUTCFullYear(oneYearFromNow.getUTCFullYear() + 1);
            if (maxDate < oneYearFromNow) {
                maxDate = oneYearFromNow;
            } else {
                maxDate.setUTCMonth(maxDate.getUTCMonth() + 3); // Add 3 months buffer beyond last task
            }

            // Calculate default kick-off date (same as project start)
            const defaultKickOff = projectStart ? new Date(projectStart) : null;

            return { minDate, maxDate, defaultKickOff, projectStart, projectEnd };
        }
    };

    // Completed main tasks drop out when the toggle is on. Their subtask rows go
    // with them (rows are only built for expanded parents) and their dependency
    // arrows disappear too, since DependencyArrows skips links whose source or
    // target row is absent. Closed subtasks of an OPEN main task drop in the
    // rows memo below - see the comment there for why it happens after the build.
    const visibleTasks = useMemo(
        () => hideCompleted ? tasks.filter(t => t.isClosed !== true) : tasks,
        [tasks, hideCompleted]
    );

    // Build hierarchical row data
    // kickOffDate and projectBounds are read by calendarDateHelpers.calculateDateRange
    // (the Project Overview bar), so a change in either must rebuild the rows
    const { rows, dateRange } = useMemo(() => {
        // Flat views build with their own expansion (a collapsed group would
        // stop its tasks being built), then filter the tree down - see
        // gantt/viewModes.js.
        const built = buildGanttRows(visibleTasks, statuses, epics, subtasks, expansionFor(viewMode, expandedItems), calendarDateHelpers);
        const shaped = applyViewMode(built.rows, viewMode, laneFilter, teamSwimlaneId);
        // Closed SUBTASKS drop here, AFTER the build, never before it. Parent
        // rows take their span and their percentage from the children handed to
        // buildGanttRows, so filtering earlier would draw a main task with
        // 3 of 4 subtasks done as a short bar reading 0%.
        //
        // Until 2026-08-23 the toggle filtered main tasks only, so an open main
        // task kept showing every completed subtask under it - the button hid
        // half of what its label promised.
        return {
            rows: hideCompleted
                ? shaped.filter(r => !(r.type === 'subtask' && r.isClosed === true))
                : shaped,
            dateRange: built.dateRange
        };
    },
        [visibleTasks, hideCompleted, statuses, epics, subtasks, expandedItems, kickOffDate, projectBounds, taskStatuses, viewMode, laneFilter, teamSwimlaneId]
    );

    // Where Follow mode points. The two axes come from DIFFERENT rows (D2, D27,
    // Bill 2026-08-23) and that is the whole point of this memo:
    //
    //   horizontal - the earliest start date of any open subtask, anywhere in
    //                the chart. Close it and the left edge walks right.
    //   vertical   - the FIRST open subtask in row order, and the group header
    //                above it (D4). The chart only walks DOWN once everything
    //                above is closed.
    //
    // One row used to drive both, so an open subtask far down the plan that
    // happened to start a day earlier took the viewport with it and scrolled
    // still-open work off the top of the screen. Row order is not date order,
    // and both are wanted at once - they are independent axes, so they get
    // independent accumulators.
    //
    // Ties on start date are free horizontally: same date is the same column.
    const followAnchor = useMemo(() => {
        if (!followMode) return null;
        let earliest = null;      // horizontal
        let firstIndex = -1;      // vertical
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (r.type !== 'subtask' || r.isClosed === true) continue;
            const raw = r.start || r.end;          // no start, use end
            if (!raw) continue;                     // neither: not anchorable
            if (firstIndex === -1) firstIndex = i;
            const t = new Date(raw).getTime();
            if (earliest === null || t < earliest) earliest = t;
        }
        if (firstIndex === -1) return null;

        // Walk up to the group header above the first open subtask (D4).
        // Deliberately by row type rather than a parent id: the status-grouped
        // and epic-grouped builds carry different parent fields. The flat lane
        // views strip headers entirely, so the walk finds none and the subtask
        // row stays the target - correct, since there is no header to show.
        let top = firstIndex;
        for (let i = firstIndex; i >= 0; i--) {
            if (rows[i].type === 'epic' || rows[i].type === 'status') { top = i; break; }
        }
        return { time: earliest, topIndex: top };
    }, [followMode, rows]);

    // Date navigation edge tracking (day and week scales only - the controls are
    // hidden on month, so the listener is not attached there either). Must sit
    // below the rows/dateRange useMemo: a dependency array is evaluated during
    // render, so referencing rows.length above the declaration is a TDZ error.
    //
    // Only two booleans are tracked, and setNavEdges returns the previous object
    // unless one of them actually flips. A naive setState(scrollLeft) here would
    // re-render a chart that also draws dependency arrows, on every scroll frame.
    useEffect(() => {
        if (currentScale === 'month') return undefined;
        const el = scrollContainerRef.current;
        if (!el) return undefined;

        let frame = null;
        const measure = () => {
            frame = null;
            const maxScroll = el.scrollWidth - el.clientWidth;
            const atStart = el.scrollLeft <= 0;
            const atEnd = maxScroll <= 0 || el.scrollLeft >= maxScroll - 1;
            setNavEdges(prev => (prev.atStart === atStart && prev.atEnd === atEnd)
                ? prev
                : { atStart, atEnd });
        };
        const onScroll = () => {
            if (frame === null) frame = requestAnimationFrame(measure);
        };

        measure();
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, [currentScale, scrollContainerRef, zoomLevel, rows.length]);

    // One footer-slider notch = one day. The input's domain is percent, so the
    // step is the percent one day is worth. Left to the HTML default of 1 it
    // moved 1% of the whole range - and calculateDateRange pads a month back and
    // a year forward, so 1% is about four days. That read as a broken day scale.
    //
    // maxScroll only moves with zoom, scale or row count, never with scrolling,
    // so this measures on those and stays out of the scroll listener above.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const maxScroll = el.scrollWidth - el.clientWidth;
        const scrollableDays = pxPerDay > 0 ? maxScroll / pxPerDay : 0;
        setSliderStep(scrollableDays >= 1 ? 100 / scrollableDays : 100);
    }, [currentScale, zoomLevel, rows.length, dateRange, scrollContainerRef]);

    // Auto-fit whenever the CURRENT SCALE has no stored zoom - first load, and
    // the first visit to each scale thereafter. This is the old isFirstLoad
    // effect widened: a single boolean could not answer "have I fitted THIS
    // scale yet", and adding a second effect would have raced this one on mount.
    // handleFitAll stores the (clamped) result, so it runs once per scale.
    useEffect(() => {
        if (hasZoomForScale || rows.length === 0 || !scrollContainerRef.current) return;
        // Small delay to ensure DOM is fully rendered
        const timer = setTimeout(() => {
            // Frames the WHOLE project (Bill, 2026-07-29)
            //
            // While following, this fit must not be what the viewer is left
            // looking at - activation forces Day scale, so on a browser that
            // has never used Day this fires 100ms AFTER the anchor scroll and
            // would undo it. Two branches to cover, because handleFitAll has
            // two: the deferred one is marked for the layout effect to
            // override, and the same-zoom one writes scrollLeft synchronously
            // and never re-renders, so it is corrected here and now.
            //
            // ⛔ THE HORIZONTAL HALF ONLY (T386). This is not a button press,
            // and the two-axis version would overwrite the user's persisted
            // reading size on every first load.
            if (followMode) autoFitPendingRef.current = true;
            fitAllHorizontal();
            if (followMode && pendingScrollRef.current === null) applyFollowScroll();
        }, 100);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows.length, currentScale, hasZoomForScale, hasAnyZoom]);

    // Toggle expand/collapse
    const toggleExpand = (rowId) => {
        setExpandedItems(prev => {
            // Default is expanded (true), so if key doesn't exist or is true, set to false
            // If key is false, set to true
            const currentState = prev[rowId] !== false; // true if expanded or undefined
            return {
                ...prev,
                [rowId]: !currentState
            };
        });
    };

    // Bulk expand/collapse by category
    const toggleAllByType = (type, swimlaneFilter = null) => {
        setExpandedItems(prev => {
            const updates = {};
            let hasAnyExpanded = false;

            // Find relevant items based on type
            if (type === 'status') {
                // Toggle status groups AND epic groups (both are purple bars)
                statuses.forEach(status => {
                    const key = `status_${status.id}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null; // Will be set below
                });

                // Also toggle actual epic groups within statuses
                const epicIds = new Set();
                tasks.forEach(task => {
                    if (task.statusId) epicIds.add(task.statusId);
                });

                epicIds.forEach(epicId => {
                    const key = `epic_${epicId}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null; // Will be set below
                });
            } else if (type === 'task') {
                tasks.forEach(task => {
                    // swimlaneFilter: 'team' for client tasks, 'tme' for Migration Experts, null for all
                    if (swimlaneFilter === 'team' && task.swimlaneId !== teamSwimlaneId) return;
                    if (swimlaneFilter === 'tme' && task.swimlaneId === teamSwimlaneId) return;
                    const key = `task_${task.projectId || 'default'}_${task.taskId}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null; // Will be set below
                });
            }

            // Set all to opposite of current majority state
            const newState = hasAnyExpanded ? false : true;
            Object.keys(updates).forEach(key => {
                updates[key] = newState;
            });

            return { ...prev, ...updates };
        });
    };

    // Handle row reorder via drag-and-drop (works for statuses, tasks, and subtasks)
    const handleRowReorder = (dragInfo) => {
        if (!dragInfo.orderKey) return;
        if (!dragInfo.targetRowId) return; // No target
        if (dragInfo.sourceRowId === dragInfo.targetRowId) return; // Same item (use unique row ID)

        // Get current items for this orderKey in their current order
        const orderKey = dragInfo.orderKey;
        let currentItems;
        let isMainTaskReorder = false;

        if (orderKey === '_statuses') {
            // Reordering statuses
            currentItems = rows.filter(r => r.type === 'status');
        } else if (orderKey === '_epics') {
            // Reordering epics
            currentItems = rows.filter(r => r.type === 'epic');
        } else if (orderKey === '_root') {
            // Reordering root tasks (no status)
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === '_root');
            isMainTaskReorder = true;
        } else if (orderKey.startsWith('_status_')) {
            // Reordering tasks within a status
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === orderKey);
            isMainTaskReorder = true;
        } else if (orderKey.startsWith('_epic_')) {
            // Reordering tasks within an epic
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === orderKey);
            isMainTaskReorder = true;
        } else {
            // Reordering subtasks (original behaviour - mainTaskId is the key)
            // Handle both numeric and string (MT#0001) IDs, and check projectId
            currentItems = rows.filter(r => r.type === 'subtask' && String(r.mainTaskId) === String(orderKey) && r.projectId === dragInfo.projectId);
        }

        // Check we have the right callback
        if (isMainTaskReorder && !onMainTaskOrderChange) return;
        if (!isMainTaskReorder && !onSubtaskOrderChange) return;

        // For main task reorder, use composite key (projectId:taskId) to handle templates with same task IDs
        const currentOrder = isMainTaskReorder
            ? currentItems.map(s => `${s.projectId}:${s.originalId}`)
            : currentItems.map(s => s.originalId);

        // Find source and target indices in original array
        const sourceKey = isMainTaskReorder
            ? `${dragInfo.projectId}:${dragInfo.sourceOriginalId}`
            : dragInfo.sourceOriginalId;
        // For target, use targetRowId (unique row.id) to find the correct row
        const targetRow = currentItems.find(r => r.id === dragInfo.targetRowId);
        const targetKey = isMainTaskReorder
            ? targetRow ? `${targetRow.projectId}:${targetRow.originalId}` : null
            : dragInfo.targetOriginalId;

        const sourceIdx = currentOrder.indexOf(sourceKey);
        const targetIdx = currentOrder.indexOf(targetKey);
        if (sourceIdx === -1 || targetIdx === -1) return;

        // Remove from source position and insert at target position
        const [removed] = currentOrder.splice(sourceIdx, 1);
        currentOrder.splice(targetIdx, 0, removed);

        if (isMainTaskReorder && onMainTaskOrderChange) {
            // For main task reorder, extract epicId
            // orderKey is like "_epic_1" or "_status_1", epicId is the number after underscore
            const epicId = orderKey.split('_').pop();
            // currentOrder already has "projectId:taskId" format
            onMainTaskOrderChange(epicId, currentOrder);
        } else {
            // Call the callback to save the new order (include projectId for template mode)
            onSubtaskOrderChange(orderKey, currentOrder, dragInfo.projectId);
        }
        setReorderDrag(null);
    };

    // Calculate columns based on date range and scale
    const { columns, monthSpans, pixelsPerDay, columnWidth } = useMemo(() => {
        const { minDate, maxDate } = dateRange;

        const cols = [];
        let currentDate = new Date(minDate);

        if (currentScale === 'day') {
            while (currentDate <= maxDate) {
                cols.push({
                    date: new Date(currentDate),
                    label: currentDate.getUTCDate().toString(),
                    month: currentDate.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short', year: 'numeric' }),
                    isWeekend: currentDate.getUTCDay() === 0 || currentDate.getUTCDay() === 6,
                    isToday: currentDate.getTime() === utcToday().getTime()
                });
                currentDate.setUTCDate(currentDate.getUTCDate() + 1);
            }
        } else if (currentScale === 'week') {
            // Align to Monday
            const day = currentDate.getUTCDay();
            currentDate.setUTCDate(currentDate.getUTCDate() - (day === 0 ? 6 : day - 1));
            while (currentDate <= maxDate) {
                const weekStart = new Date(currentDate);
                cols.push({
                    date: weekStart,
                    label: `W${getWeekNumber(weekStart)}`,
                    month: weekStart.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short', year: 'numeric' }),
                    isWeekend: false,
                    isToday: false
                });
                currentDate.setUTCDate(currentDate.getUTCDate() + 7);
            }
        } else {
            // Month scale - no need for month spans row
            currentDate.setUTCDate(1);
            while (currentDate <= maxDate) {
                cols.push({
                    date: new Date(currentDate),
                    label: currentDate.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short', year: 'numeric' }),
                    month: null,
                    isWeekend: false,
                    isToday: false
                });
                currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
            }
        }

        // Calculate column width based on zoom
        const { colWidth, pxPerDay } = getColumnMetrics(currentScale, zoomLevel);

        // Calculate month spans for the top header row (day and week scales only)
        const spans = [];
        if (currentScale !== 'month' && cols.length > 0) {
            let currentMonth = cols[0].month;
            let spanStart = 0;
            let spanCount = 0;

            for (let i = 0; i < cols.length; i++) {
                if (cols[i].month === currentMonth) {
                    spanCount++;
                } else {
                    spans.push({ label: currentMonth, startIndex: spanStart, count: spanCount });
                    currentMonth = cols[i].month;
                    spanStart = i;
                    spanCount = 1;
                }
            }
            // Add last span
            spans.push({ label: currentMonth, startIndex: spanStart, count: spanCount });
        }

        return {
            columns: cols,
            monthSpans: spans,
            pixelsPerDay: pxPerDay,
            columnWidth: colWidth
        };
    }, [dateRange, currentScale, containerWidth, zoomLevel]);

    pixelsPerDayRef.current = pixelsPerDay;

    // Calculate bar position for a row
    const getBarPosition = (row) => {
        if (!row.start) return null;

        const start = new Date(row.start);
        const end = row.end ? new Date(row.end) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

        const startOffset = (start - dateRange.minDate) / (1000 * 60 * 60 * 24);
        const duration = Math.max((end - start) / (1000 * 60 * 60 * 24) + 1, 1);

        return {
            left: startOffset * pixelsPerDay,
            width: Math.max(duration * pixelsPerDay, 20)
        };
    };

    // The pair a drag is measured from. Shared by handleDragStart and by the
    // dismissal check (D16), so "has this row changed since the finger went
    // down" is asked in exactly the terms the drag was started in - a row with
    // no dates drags from today, and must not read as "changed" a tick later.
    const dragDatesFor = (row) => {
        const start = row.start ? new Date(row.start) : utcToday();
        const end = row.end ? new Date(row.end) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        return { start, end };
    };

    // Drag handlers
    const handleDragStart = (e, row) => {
        // Block if dependency drag is active or shift key pressed
        if (dependencyDragRef.current) {
            return;
        }
        if (readOnly) {
            return;
        }
        // Allow parent tasks (isSummary) but not statuses or epics
        if (row.type === 'status' || row.type === 'epic') {
            return;
        }
        if (e.shiftKey) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const barWidth = rect.width;

        // ⛔ THE GRIP IS ANSWERED PER EVENT, NEVER PER DEVICE (T369). 44px for a
        // finger, 12px for a mouse or a pen - so an iPad with a trackpad gets
        // both answers in the same session, which no breakpoint could do.
        //
        // ⛔ AND THIS IS THE MEASUREMENT THE WHOLE CSS-SCALE DECISION RESTS ON.
        // clickX comes from a rect and barWidth comes from a rect, so they are
        // in the SAME space. Under a CSS zoom or transform they would not be -
        // rects scale, layout numbers do not - and a press on a bar's exact
        // centre came back as resize-right, i.e. moving a task would have
        // resized it. That is why uiScale multiplies real numbers instead.
        const dragType = dragTypeFor(clickX, barWidth, e.pointerType);

        const { start: taskStart, end: taskEnd } = dragDatesFor(row);

        const newDragState = {
            rowId: row.id,
            originalId: row.originalId,
            projectId: row.projectId,
            ref: row.ref,
            dragType,
            startX: e.clientX,
            originalStart: taskStart,
            originalEnd: taskEnd,
            // ⛔ THE ELEMENT, NOT ITS RECT (T402, §7.3). The confirmation is
            // anchored at RELEASE, and a rect measured now is stale by the whole
            // drag distance. The hold's status popup keeps the element for the
            // same reason - it opens from a timer, long after the event is any
            // use.
            anchorEl: e.currentTarget,
            // ⛔ For the release gate: a TOUCH release that never travelled past
            // TOUCH_TAP_SLOP commits nothing (the ladder's tap band). The mouse
            // is exempt - its commit rule is deltaDays alone, unchanged.
            pointerType: e.pointerType,
            movedPastSlop: false
        };

        didDragRef.current = false;
        dragStateRef.current = newDragState;
        dragPreviewRef.current = null;
        setDragState(newDragState);
        setDragPreview(null);
    };

    // The five lines every end path runs. Named because there are now six of
    // them - the tap band, a dependency drag, the blocked alert, three declined
    // dialogs and the ordinary finish - and they were drifting.
    const clearDragPreviews = () => {
        setDragState(null);
        setDragPreview(null);
        dragPreviewRef.current = null;
        setPushedTaskPreview(null);
        pushedTaskPreviewRef.current = null;
    };

    // ⛔ THE RELEASE IS TWO FUNCTIONS, AND THE SPLIT IS THE DESIGN (T401,
    // Planning/GANTT-DRAG-CONFIRM-2026-08-30.md §6.2). planDragCommit works out
    // WHAT a release would write - including the dependency clamp a parent move
    // only learns about after the finger has lifted - and writes nothing.
    // applyDragCommit does the writing.
    //
    // ⛔ planDragCommit MUST NOT WRITE AND MUST NOT PROMPT. No window.confirm,
    // no window.alert, no onTaskUpdate, no positionOverridesRef. Its result is
    // either applied at once (a mouse) or held in state until a finger answers
    // for it (T402) - and a prompt inside it would fire BEFORE the question the
    // whole feature exists to ask.
    //
    // ⛔ AND BOTH LIVE AT COMPONENT SCOPE, NOT INSIDE THE DRAG EFFECT BELOW: the
    // confirmation's onConfirm calls applyDragCommit from the render tree, which
    // cannot reach a function declared in that effect's closure.
    const planDragCommit = (currentDragState, currentDragPreview) => {
        if (!currentDragState || !currentDragPreview) return null;

        const dragRow = rows.find(r => r.id === currentDragState.rowId);
        const overrideKey = `${currentDragState.projectId || 'default'}:${currentDragState.originalId}`;

        // ⚠️ `was` COMES OFF THE DRAG STATE, NOT OFF THE ROW. They agree today,
        // but originalStart/originalEnd were captured at pointerdown and are the
        // only copy that still means "before" once anything else has written the
        // row mid-gesture.
        const base = {
            row: dragRow,
            dragType: currentDragState.dragType,
            was: {
                start: formatDateYMD(currentDragState.originalStart),
                end: formatDateYMD(currentDragState.originalEnd)
            },
            overrideKey,
            projectId: currentDragState.projectId,
            originalId: currentDragState.originalId
        };

        // A parent move takes every subtask with it, and its FS group clamp is
        // computed HERE rather than at write time (D3) - so the question a
        // finger is asked prints the dates that will actually be saved.
        if (dragRow?.isSummary && dragRow?.type === 'task' && onParentTaskMove) {
            const deltaDays = Math.round(
                (currentDragPreview.start - currentDragState.originalStart) / (1000 * 60 * 60 * 24)
            );
            const parentSubtasks = rows.filter(r =>
                r.type === 'subtask' && r.mainTaskId === dragRow.originalId && r.projectId === dragRow.projectId
            );

            // FS GROUP CLAMP (Bill's rules): a parent move is a MOVE - the whole
            // group stops at the first EXTERNAL dependency boundary in the drag
            // direction. Internal links shift together and cannot break.
            let effDelta = deltaDays;
            let boundByName = null;
            if (deltaDays !== 0 && parentSubtasks.length > 0) {
                const { ctx, rowByKey } = buildFsCtx();
                const groupKeys = new Set(parentSubtasks.map(r => `${r.ref}|${r.projectId || ''}`));
                let minDelta = -Infinity;
                let maxDelta = Infinity;
                for (const edge of ctx.edges) {
                    const sIn = groupKeys.has(edge.source);
                    const tIn = groupKeys.has(edge.target);
                    if (sIn === tIn) continue; // internal or unrelated
                    if (tIn) {
                        // External source feeds a member: bound leftward moves
                        const m = ctx.tasksByKey[edge.target];
                        const srcEnd = ctx.tasksByKey[edge.source].end;
                        const bound = (srcEnd + 1) - m.start;
                        if (bound > minDelta) {
                            minDelta = bound;
                            if (deltaDays < 0) boundByName = rowByKey[edge.source]?.text;
                        }
                    } else {
                        // A member feeds an external target: bound rightward moves
                        const m = ctx.tasksByKey[edge.source];
                        const tgtStart = ctx.tasksByKey[edge.target].start;
                        const bound = (tgtStart - 1) - m.end;
                        if (bound < maxDelta) {
                            maxDelta = bound;
                            if (deltaDays > 0) boundByName = rowByKey[edge.target]?.text;
                        }
                    }
                }
                if (deltaDays > 0) effDelta = Math.max(0, Math.min(deltaDays, maxDelta));
                else effDelta = Math.min(0, Math.max(deltaDays, minDelta));
            }

            // The parent's own pair, from the APPLIED delta. When nothing
            // clamped this is identical to the preview; when something did, it
            // is the shorter move, which is the whole point of D3.
            const cs = new Date(currentDragState.originalStart);
            cs.setUTCDate(cs.getUTCDate() + effDelta);
            const ce = new Date(currentDragState.originalEnd);
            ce.setUTCDate(ce.getUTCDate() + effDelta);

            return {
                ...base,
                kind: 'parent',
                next: { start: formatDateYMD(cs), end: formatDateYMD(ce) },
                // ⛔ deltaDays travels INSIDE blocked as well as at the top
                // level. The alert's wording branches on the direction of the
                // attempted move, and that branch never reaches applyDragCommit.
                blocked: effDelta === 0 ? { name: boundByName, deltaDays } : null,
                clampedBy: effDelta !== deltaDays ? { name: boundByName } : null,
                // "moves N subtasks" counts only the ones that MOVE - the write
                // loop skips a subtask holding neither date.
                affectedCount: parentSubtasks.filter(st => st.start || st.end).length,
                deltaDays,
                effDelta,
                parentSubtasks,
                pushedTasks: null
            };
        }

        // The single-row path did nothing at all without onTaskUpdate, and still
        // does not: no handler, no plan.
        if (!onTaskUpdate) return null;

        const pushedTasks = pushedTaskPreviewRef.current;
        return {
            ...base,
            kind: 'single',
            next: {
                start: formatDateYMD(currentDragPreview.start),
                end: formatDateYMD(currentDragPreview.end)
            },
            blocked: null,
            clampedBy: null,
            affectedCount: pushedTasks ? pushedTasks.length : 0,
            // ⛔ null, not 0. A single-row drag has no delta the write path
            // reads, and 0 would read as "it did not move" to anyone holding a
            // plan object.
            deltaDays: null,
            effDelta: null,
            parentSubtasks: null,
            pushedTasks
        };
    };

    const applyDragCommit = (plan) => {
        // ⛔ THE FIRST STATEMENT, BEFORE ANY DIALOG. Each dialog below DELETES
        // this key when the user declines, so writing it later would turn those
        // deletes into no-ops and leave the bar where the user just refused to
        // put it. It is also what holds the bar in place across the round trip
        // once a finger has said yes (T402).
        positionOverridesRef.current[plan.overrideKey] = { ...plan.next };

        const abort = () => {
            delete positionOverridesRef.current[plan.overrideKey];
            clearDragPreviews();
        };

        const dragRow = plan.row;

        if (plan.kind === 'parent') {
            if (plan.clampedBy) {
                // Clamped short: user confirms the limited move
                const proceed = window.confirm(
                    `This move is limited by a dependency.\n\n` +
                    `"${plan.clampedBy.name || 'A linked task'}" limits the move to ${Math.abs(plan.effDelta)} day(s) ` +
                    `instead of ${Math.abs(plan.deltaDays)}.\n\n` +
                    `Apply the limited move?`
                );
                if (!proceed) {
                    abort();
                    return;
                }
                // ⚠️ No override rewrite here. The original stored the UNCLAMPED
                // pair earlier and had to correct it at this point; plan.next is
                // already the stopped position (D3), so the write above was
                // right the first time.
            }

            // Project window warning BEFORE anything is applied, on the APPLIED
            // delta. Start is taken as start || end so a subtask holding only a
            // due date is still counted, matching the server's tasksByKey
            // normalisation.
            let earliestShifted = null;
            for (const st of plan.parentSubtasks) {
                const s = st.start || st.end;
                if (!s) continue;
                const shifted = new Date(new Date(s).getTime() + plan.effDelta * 24 * 60 * 60 * 1000);
                const ymd = formatDateYMD(shifted);
                if (!earliestShifted || ymd < earliestShifted) earliestShifted = ymd;
            }
            const parentWindow = confirmProjectWindow({
                start: earliestShifted,
                dueDate: null,
                kickoffDate: kickOffDate,
                label: dragRow.text || 'This task'
            });
            if (!parentWindow.proceed) {
                abort();
                return;
            }
            const parentMoveOverride = parentWindow.override;

            // Weekend-alignment warning BEFORE anything is applied, on the
            // APPLIED delta. Cancel leaves chart and data untouched. Suppressed
            // when the project window warning already fired - one decision per
            // drag (Bill, 2026-08-03).
            if (plan.effDelta % 7 !== 0 && !parentMoveOverride) {
                const proceed = window.confirm(
                    `Warning: Moving by ${plan.effDelta} days will change which days are weekends within the task span.\n\n` +
                    `This may cause weekend gaps to appear in different positions.\n\n` +
                    `To preserve weekend alignment, move by full weeks (7, 14, 21 days) or use the Project Shift feature.\n\n` +
                    `Continue anyway?`
                );
                if (!proceed) {
                    abort();
                    return;
                }
            }

            // Store overrides for all affected subtasks using original drag position
            // (don't use st.start/st.end as those might be from optimistic state)
            plan.parentSubtasks.forEach(st => {
                // Calculate new position from DRAG originalStart, not from current row data
                const subtaskOrigStart = st.start ? new Date(st.start) : null;
                const subtaskOrigEnd = st.end ? new Date(st.end) : null;
                // Only calculate if subtask has dates
                if (subtaskOrigStart || subtaskOrigEnd) {
                    const stNewStart = subtaskOrigStart ? new Date(subtaskOrigStart.getTime() + plan.effDelta * 24 * 60 * 60 * 1000) : null;
                    const stNewEnd = subtaskOrigEnd ? new Date(subtaskOrigEnd.getTime() + plan.effDelta * 24 * 60 * 60 * 1000) : null;
                    const stOverrideKey = `${st.projectId || 'default'}:${st.originalId}`;
                    positionOverridesRef.current[stOverrideKey] = {
                        start: stNewStart ? formatDateYMD(stNewStart) : null,
                        end: stNewEnd ? formatDateYMD(stNewEnd) : null
                    };
                }
            });
            // Fire and forget - position override handles display
            onParentTaskMove(dragRow.originalId, plan.effDelta, plan.parentSubtasks, parentMoveOverride);
        } else {
            // Project window warning before anything is committed. Pushed rows
            // only ever move later, so only the dragged row is checked.
            const dragWindow = confirmProjectWindow({
                start: plan.next.start,
                dueDate: plan.next.end,
                kickoffDate: kickOffDate,
                label: dragRow?.text || 'This task'
            });
            if (!dragWindow.proceed) {
                abort();
                return;
            }

            // Commit pushed tasks FIRST, deepest first (reverse discovery
            // order), so every write is valid against the previous one -
            // required for the server-side 409 backstop
            const pushedTasks = plan.pushedTasks;
            if (pushedTasks && pushedTasks.length > 0) {
                [...pushedTasks].reverse().forEach(p => {
                    const ps = formatDateYMD(p.start);
                    const pe = formatDateYMD(p.end);
                    positionOverridesRef.current[`${p.projectId || 'default'}:${p.originalId}`] = {
                        start: ps,
                        end: pe
                    };
                    onTaskUpdate(p.originalId, {
                        start: ps,
                        end: pe,
                        isSubtask: true,
                        projectId: p.projectId
                    });
                });
            }

            // Fire off updates - position override handles display
            onTaskUpdate(plan.originalId, {
                start: plan.next.start,
                end: plan.next.end,
                isSubtask: dragRow?.type === 'subtask',
                projectId: plan.projectId,
                allowBeforeKickoff: dragWindow.override
            });

            // Update parent task position override if this is a subtask resize
            if (dragRow?.type === 'subtask' && dragRow?.mainTaskId) {
                const parentRow = rows.find(r => r.originalId === dragRow.mainTaskId && r.type === 'task' && r.projectId === dragRow.projectId);
                if (parentRow) {
                    // Calculate new parent bounds from all subtasks including this override
                    const siblingSubtasks = rows.filter(r => r.type === 'subtask' && r.mainTaskId === dragRow.mainTaskId && r.projectId === dragRow.projectId);
                    let minStart = null;
                    let maxEnd = null;
                    for (const sibling of siblingSubtasks) {
                        // Use override for the resized subtask, original for others
                        const siblingStart = sibling.originalId === dragRow.originalId
                            ? new Date(plan.next.start)
                            : (sibling.start ? new Date(sibling.start) : null);
                        const siblingEnd = sibling.originalId === dragRow.originalId
                            ? new Date(plan.next.end)
                            : (sibling.end ? new Date(sibling.end) : null);
                        if (siblingStart && (!minStart || siblingStart < minStart)) minStart = siblingStart;
                        if (siblingEnd && (!maxEnd || siblingEnd > maxEnd)) maxEnd = siblingEnd;
                    }
                    if (minStart && maxEnd) {
                        const parentOverrideKey = `${parentRow.projectId || 'default'}:${dragRow.mainTaskId}`;
                        positionOverridesRef.current[parentOverrideKey] = {
                            start: formatDateYMD(minStart),
                            end: formatDateYMD(maxEnd)
                        };
                    }
                }
            }
        }

        // Clear drag state - position overrides will show correct position
        clearDragPreviews();
    };

    // ⛔ THE QUESTION DOES NOT OUTLIVE THE ROW IT IS ABOUT (D16, §6.6). Every
    // other dialog on this path is a window.confirm, which freezes the tab - so
    // until now nothing could change between a release and its write. A React
    // popup does not freeze anything: a WebSocket refresh lands, another admin
    // edits or deletes the task, and "Move it" would write dates worked out
    // before any of that.
    //
    // ⛔ ROW-SCOPED, NOT BLANKET (Bill's ruling of the three options offered).
    // Unrelated traffic - a comment, someone closing something elsewhere -
    // leaves an open question alone; only the DRAGGED row changing or vanishing
    // dismisses it.
    //
    // ⚠️ COMPARED AGAINST `was`, the pre-drag pair captured at pointerdown, so
    // "still equal" means nobody else has touched this row since the finger went
    // down. Comparing against `next` would compare the answer with the question.
    //
    // ⚠️ AND IT NEVER READS positionOverridesRef. That is this chart's own
    // optimistic copy; a PREVIOUS drag's override on the same row would make it
    // look changed and dismiss a question for no reason.
    useEffect(() => {
        if (!dragConfirm) return;
        const row = rows.find(r =>
            r.originalId === dragConfirm.originalId &&
            (r.projectId || null) === (dragConfirm.projectId || null)
        );
        if (row) {
            const { start, end } = dragDatesFor(row);
            if (formatDateYMD(start) === dragConfirm.was.start &&
                formatDateYMD(end) === dragConfirm.was.end) return;
        }
        setDragConfirm(null);
        clearDragPreviews();
        // What this watches is the ROW under an open question. The two helpers
        // are redeclared every render and listing them would re-run it on every
        // render, which is the opposite of what it is for.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, dragConfirm]);

    // D4. The chart owns the wording, because the two kinds are different
    // sentences - and null means the popup renders no line at all, since an
    // empty "0 linked tasks" is noise on a card meant to be read in a glance.
    const dragConfirmCountLabel = !dragConfirm || dragConfirm.affectedCount === 0
        ? null
        : dragConfirm.kind === 'parent'
            ? `moves ${dragConfirm.affectedCount} subtask${dragConfirm.affectedCount === 1 ? '' : 's'}`
            : `${dragConfirm.affectedCount} linked task${dragConfirm.affectedCount === 1 ? '' : 's'} also move${dragConfirm.affectedCount === 1 ? 's' : ''}`;

    // Global mouse listeners for dragging
    useEffect(() => {
        if (!dragState) return;

        const handleMouseMove = (e) => {
            // Block if shift is held or dependency drag is active
            if (e.shiftKey || dependencyDragRef.current) return;

            const currentDragState = dragStateRef.current;
            if (!currentDragState) return;

            const deltaX = e.clientX - currentDragState.startX;
            if (Math.abs(deltaX) > 3) didDragRef.current = true;
            if (Math.abs(deltaX) > TOUCH_TAP_SLOP) currentDragState.movedPastSlop = true;
            const deltaDays = Math.round(deltaX / pixelsPerDayRef.current);

            let newStart = new Date(currentDragState.originalStart);
            let newEnd = new Date(currentDragState.originalEnd);

            if (currentDragState.dragType === 'move') {
                newStart.setUTCDate(newStart.getUTCDate() + deltaDays);
                newEnd.setUTCDate(newEnd.getUTCDate() + deltaDays);
            } else if (currentDragState.dragType === 'resize-left') {
                newStart.setUTCDate(newStart.getUTCDate() + deltaDays);
                if (newStart > newEnd) {
                    newStart = new Date(newEnd);
                }
            } else if (currentDragState.dragType === 'resize-right') {
                newEnd.setUTCDate(newEnd.getUTCDate() + deltaDays);
                if (newEnd < newStart) {
                    newEnd = new Date(newStart);
                }
            }

            // --- FS CONSTRAINTS (Bill's rules, 2026-07-29) ---
            // MOVE: clamp at the boundary, nothing else moves.
            // RESIZE: push - all links, full chain, gaps preserved.
            let pushedTasks = null;
            const activeRow = rows.find(r => r.id === currentDragState.rowId);
            if (activeRow && activeRow.type === 'subtask') {
                const { ctx, rowByKey } = buildFsCtx();
                const selfKey = `${activeRow.ref}|${activeRow.projectId || ''}`;

                if (ctx.tasksByKey[selfKey]) {
                    const dayStart = toDayNum(formatDateYMD(newStart));
                    const dayEnd = toDayNum(formatDateYMD(newEnd));
                    if (currentDragState.dragType === 'move') {
                        const clamped = clampMove(ctx, selfKey, dayStart, dayEnd);
                        newStart = new Date(fromDayNum(clamped.start) + 'T00:00:00Z');
                        newEnd = new Date(fromDayNum(clamped.end) + 'T00:00:00Z');
                    } else {
                        const { changes, order } = cascadeResize(ctx, selfKey, dayStart, dayEnd);
                        pushedTasks = order.slice(1).map(k => {
                            const row = rowByKey[k];
                            return {
                                rowId: row.id,
                                originalId: row.originalId,
                                projectId: row.projectId,
                                start: new Date(fromDayNum(changes[k].start) + 'T00:00:00Z'),
                                end: new Date(fromDayNum(changes[k].end) + 'T00:00:00Z')
                            };
                        });
                        if (pushedTasks.length === 0) pushedTasks = null;
                    }
                }
            }
            pushedTaskPreviewRef.current = pushedTasks;
            setPushedTaskPreview(pushedTasks);
            // --- END FS CONSTRAINTS ---

            const newPreview = {
                rowId: currentDragState.rowId,
                start: newStart,
                end: newEnd
            };
            dragPreviewRef.current = newPreview;
            setDragPreview(newPreview);
        };

        // ⛔ THE EVENT IS TAKEN NOW, AND IT IS THE ONLY LIVE SOURCE OF THE
        // RELEASE POINT (T402 anchors the confirmation on it - §7.3). A rect
        // captured at pointerdown is stale by the whole drag distance.
        const handleMouseUp = (e) => {
            const currentDragState = dragStateRef.current;
            const currentDragPreview = dragPreviewRef.current;

            // ⛔ IDEMPOTENT. pointerup and lostpointercapture BOTH fire on an
            // ordinary release - the browser releases capture implicitly - so
            // this runs TWICE every time. Without the early return the second
            // run would re-enter the commit path with a stale preview.
            if (!currentDragState) return;

            // Clear refs immediately
            dragStateRef.current = null;
            didDragRef.current = false;

            // ⛔ A TOUCH RELEASE INSIDE THE TAP BAND COMMITS NOTHING (the
            // ladder's V34 rule, enforced on the COMMIT and not just the card).
            // The commit below rounds deltaX into days, and at a zoomed-out
            // scale a day is a few pixels - ungated, a finger wobble under the
            // hold threshold silently moved a task one or more days. The preview
            // is dropped and the bar goes back where it was.
            if (currentDragState.pointerType === 'touch' && !currentDragState.movedPastSlop) {
                clearDragPreviews();
                return;
            }

            // A dependency drag owns the release; nothing is committed.
            if (dependencyDragRef.current) {
                clearDragPreviews();
                return;
            }

            const plan = planDragCommit(currentDragState, currentDragPreview);
            if (!plan) {
                clearDragPreviews();
                return;
            }

            // ⛔ A FULLY BLOCKED PARENT MOVE NEVER REACHES applyDragCommit (§6.4)
            // - nothing can move, so there is nothing to write and, once T402
            // lands, nothing to ask about either. No override was written, so
            // there is none to delete.
            if (plan.blocked) {
                // Fully blocked - tell the user WHY nothing moved
                window.alert(
                    `Move blocked by a dependency.\n\n` +
                    `"${plan.blocked.name || 'A linked task'}" ${plan.blocked.deltaDays < 0 ? 'must finish before this task starts' : 'cannot start until this task finishes'} - ` +
                    `the task group cannot move ${plan.blocked.deltaDays < 0 ? 'earlier' : 'later'} at all.`
                );
                clearDragPreviews();
                return;
            }

            // ⛔ A FINGER ASKS FIRST (T402). Bill, 2026-08-30, after using the
            // chart on an iPad: "maybe worth adding a confirmation prompt when
            // you change the dates in the Gantt otherwise it just does it and
            // you don't really know what you've changed". A MOUSE COMMITS AS IT
            // ALWAYS HAS - he asked for it on the finger gestures, in those
            // words, twice.
            //
            // ⛔ ONLY dragState IS CLEARED. Both previews are left alive, so the
            // dragged bar and every pushed bar stay where the finger put them
            // while the question is on screen - the answer is given about
            // something visible, and nothing has been written to undo.
            //
            // ⛔ x IS THE RELEASE POINT AND y IS MEASURED FRESH (§7.3). The
            // hover card anchors on the pointer for a reason: a bar zoomed wider
            // than the viewport has its midpoint off screen.
            if (currentDragState.pointerType === 'touch') {
                setDragConfirm({
                    ...plan,
                    x: e.clientX,
                    y: currentDragState.anchorEl.getBoundingClientRect().top - 8
                });
                setDragState(null);
                return;
            }

            applyDragCommit(plan);
        };

        // ⛔ A CANCELLED GESTURE ABORTS - IT DOES NOT COMMIT. pointercancel is
        // iOS taking the gesture away: the left-edge back swipe, a system sheet,
        // an incoming call. The bar goes back where it was and nothing is
        // written, because the user never finished asking for anything.
        const handlePointerCancel = () => {
            if (!dragStateRef.current) return;
            dragStateRef.current = null;
            didDragRef.current = false;
            setDragState(null);
            setDragPreview(null);
            dragPreviewRef.current = null;
            setPushedTaskPreview(null);
            pushedTaskPreviewRef.current = null;
        };

        window.addEventListener('pointermove', handleMouseMove);
        window.addEventListener('pointerup', handleMouseUp);
        window.addEventListener('lostpointercapture', handleMouseUp);
        window.addEventListener('pointercancel', handlePointerCancel);
        return () => {
            window.removeEventListener('pointermove', handleMouseMove);
            window.removeEventListener('pointerup', handleMouseUp);
            window.removeEventListener('lostpointercapture', handleMouseUp);
            window.removeEventListener('pointercancel', handlePointerCancel);
        };
    }, [dragState, onTaskUpdate]);

    // Dependency drag handlers (shift+drag from subtask to subtask)
    const handleDependencyDragStart = (e, row) => {
        if (readOnly || row.type !== 'subtask') return;
        if (!e.shiftKey) return;

        e.preventDefault();
        e.stopPropagation();

        // Clear any existing task drag state to prevent accidental resize
        dragStateRef.current = null;
        dragPreviewRef.current = null;
        setDragState(null);
        setDragPreview(null);

        const rect = scrollContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const newDependencyDrag = {
            sourceRowId: row.id,
            sourceOriginalId: row.originalId,
            sourceRef: row.ref,
            sourceText: row.text,
            sourceProjectId: row.sourceTemplateId || row.projectId,
            startX: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
            startY: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
        };
        // Set ref IMMEDIATELY (sync) before state (async)
        dependencyDragRef.current = newDependencyDrag;
        setDependencyDrag(newDependencyDrag);
        setCursorPos({
            x: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
            y: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
        });
    };

    // Global handlers for dependency drag
    useEffect(() => {
        if (!dependencyDrag) return;

        const handleMouseMove = (e) => {
            const rect = scrollContainerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setCursorPos({
                x: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
                y: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
            });
        };

        const handleMouseUp = () => {
            dependencyDragRef.current = null;
            setDependencyDrag(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dependencyDrag]);

    // Handle drop on a target subtask to create dependency
    const handleDependencyDrop = (e, targetRow) => {
        if (!dependencyDrag || readOnly) return;
        if (targetRow.type !== 'subtask') return;
        if (targetRow.id === dependencyDrag.sourceRowId) return;

        e.stopPropagation();
        e.preventDefault();

        // Double-check: clear any drag state that might have snuck in
        dragStateRef.current = null;
        dragPreviewRef.current = null;

        const newLink = {
            source: dependencyDrag.sourceRef,
            target: targetRow.originalId,
            type: 'FS',
            sourceProjectId: dependencyDrag.sourceProjectId,
            targetProjectId: targetRow.sourceTemplateId || targetRow.projectId
        };

        // Add to pending links for immediate visual feedback
        setPendingLinks(prev => ({
            ...prev,
            added: [...prev.added, newLink]
        }));

        // Enqueue operation (optimistic UI)
        if (onDependencyCreate) {
            onDependencyCreate(newLink);
        }

        // Clear ref immediately, then state
        dependencyDragRef.current = null;
        setDependencyDrag(null);
    };

    // Build tooltip content for a row (returns JSX)
    const buildTooltipContent = (row) => {
        const lines = [];
        if (row.isMeeting) {
            lines.push(<div key="meeting-label"><span className="font-bold">Meeting:</span> {row.text}</div>);
            if (row.meetingType) {
                lines.push(<div key="meeting-type"><span className="font-bold">Type:</span> {row.meetingType}</div>);
            }
        } else {
            lines.push(<div key="name" className="font-bold">{row.text}</div>);
        }
        lines.push(<div key="dates">{row.start ? formatDateUK(row.start) : 'No start'} to {row.end ? formatDateUK(row.end) : 'No end'}</div>);
        if (row.assignee) {
            lines.push(<div key="assignee"><span className="font-bold">Assigned To:</span> {row.assignee}</div>);
        }

        // Show links if any
        if (row.links && row.links.length > 0) {
            lines.push(<div key="links-header" className="font-bold mt-1">Links:</div>);
            row.links.forEach((link, i) => {
                lines.push(
                    <div key={`link-${i}`} className="pl-2 truncate max-w-[250px]">
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                            {link.description}
                        </a>
                    </div>
                );
            });
        }

        // Show agenda for meetings
        if (row.isMeeting && row.agenda) {
            lines.push(<div key="agenda-header" className="font-bold mt-1">Agenda:</div>);
            const agendaPreview = row.agenda.length > 150 ? row.agenda.substring(0, 150) + '...' : row.agenda;
            lines.push(<div key="agenda-content" className="pl-2 text-xs whitespace-pre-wrap max-w-[250px] mb-1">{agendaPreview}</div>);
        }

        if (row.type === 'subtask') {
            const parentTask = rows.find(r => r.type === 'task' && r.originalId === row.mainTaskId && r.projectId === row.projectId);
            if (parentTask) {
                // The whole tree, not just the main task (Bill, 2026-08-30).
                //
                // ⛔ parentTask.status is the GROUP's name here - the epic or
                // workflow status the main task sits under (GanttRowBuilder.js
                // :171, :303) - NOT a workflow status. The same field means the
                // other thing one row down, which is why statusLabel gates on
                // row.type and this reads the parent's field rather than its own.
                const tree = parentTask.status
                    ? `${parentTask.status} > ${parentTask.text}`
                    : parentTask.text;
                lines.push(<div key="parent" className="mt-1"><span className="font-bold">Parent:</span> {tree}</div>);
            }
            // Bill: "directly after the Parent line". Omitted entirely when the
            // status resolves to nothing - "Status: Unknown" would read as a real
            // state on a card people make decisions from.
            const statusName = statusLabel(row, taskStatuses);
            if (statusName) {
                lines.push(<div key="status"><span className="font-bold">Status:</span> {statusName}</div>);
            }
            const rowRef = row.ref;
            const deps = effectiveLinks.filter(l =>
                (getNumericRef(l.source) === rowRef && (!l.sourceProjectId || l.sourceProjectId === row.projectId)) ||
                (getNumericRef(l.target) === rowRef && (!l.targetProjectId || l.targetProjectId === row.projectId))
            );
            if (deps.length > 0) {
                lines.push(<div key="deps-header" className="font-bold mt-1">Dependencies:</div>);
                deps.forEach((d, i) => {
                    const isTarget = getNumericRef(d.target) === rowRef;
                    const otherId = isTarget ? getNumericRef(d.source) : getNumericRef(d.target);
                    const otherProjectId = isTarget ? d.sourceProjectId : d.targetProjectId;
                    const otherTask = rows.find(r =>
                        r.type === 'subtask' && r.ref === otherId &&
                        (!otherProjectId || (r.sourceTemplateId || r.projectId) === otherProjectId)
                    );
                    if (isTarget) {
                        lines.push(<div key={`dep-${i}`} className="pl-2"><span className="font-bold">Depends on:</span> {otherTask?.text || otherId} ({d.type || 'FS'})</div>);
                    } else {
                        lines.push(<div key={`dep-${i}`} className="pl-2"><span className="font-bold">Blocks:</span> {otherTask?.text || otherId} ({d.type || 'FS'})</div>);
                    }
                });
            }
            // Show blocked by info
            const blockers = getBlockers(blockedTasksMap, row.originalId, row.projectId, false);
            if (blockers.length > 0) {
                lines.push(<div key="blocked-header" className="font-bold mt-1 text-red-400">Blocked by:</div>);
                blockers.forEach((b, i) => {
                    if (b.type === 'issue') {
                        lines.push(<div key={`blocker-${i}`} className="pl-2 text-red-300">Issue {b.id}: {b.title}</div>);
                    } else {
                        const depLabel = b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title;
                        lines.push(<div key={`blocker-${i}`} className="pl-2 text-red-300">{depLabel}</div>);
                    }
                });
            }
            if (!readOnly) {
                lines.push(<div key="hint" className="mt-2 text-gray-400 text-xs">Shift+drag to create dependency<br/>Ctrl+click weekend to toggle worked<br/>Ctrl+click dependency to pick colour</div>);
            }
        }
        // D20: a main task says which epic it belongs to, so every bar says where
        // it sits. NO Status line here - a main task has no workflow status of
        // its own, only a closed state derived from its subtasks. `row.status` on
        // this row is the epic's name, and printing it as a status is the trap
        // statusLabel exists to close.
        if (row.type === 'task' && row.status) {
            lines.push(<div key="parent" className="mt-1"><span className="font-bold">Parent:</span> {row.status}</div>);
        }
        if (row.isSummary && row.type === 'task' && !readOnly) {
            lines.push(<div key="hint-summary" className="mt-2 text-gray-400 text-xs">Drag to move all subtasks</div>);
        }
        return lines;
    };

    // Tooltip mouse handlers
    // ⛔ A BAR CARRIES THREE TOUCH GESTURES, AND THE LADDER IS STATED ONCE
    // (T374, 2026-08-30):
    //
    //   moves > 5px before 350ms    a DRAG - the hold is cancelled
    //   moves > 24px total          the drag ASKS on release (T402)
    //   held still for 350ms        the STATUS POPUP; the drag is cancelled and
    //                               its preview restored
    //   released under both         a TAP, which opens the hover card
    //
    // ⚠️ THE TWO THRESHOLDS ARE NOT A CONTRADICTION AND MUST NOT BE COLLAPSED.
    // 5px cancels a HOLD; 24px earns a DRAG. Between them the finger has moved
    // too far to be a hold and not far enough to be a deliberate date change.
    // They also measure DIFFERENT DISTANCES: the hold uses hypot, because a hold
    // is about the finger being still, and the drag uses deltaX alone, because a
    // bar only moves in x.
    //
    // ⚠️ The dead zone grew from 3px to 19px with T405, and its outcome is the
    // hover card: a slip shows the task's card, which is harmless and arguably
    // useful.
    const HOLD_MS = 350;
    const HOLD_SLOP = 5;
    const TAP_SLOP = TOUCH_TAP_SLOP;
    const touchBarRef = useRef(null);

    // The device that produced the LAST pointer event, for the hover guard
    // below. ⛔ Written from pointermove as well as pointerdown: on an iPad with
    // a trackpad, a tap leaves 'touch' here and a later mouse HOVER produces no
    // press at all - so a down-only ref would keep the card dead until the next
    // click, on the very hybrid device this was built for.
    const lastPointerTypeRef = useRef('mouse');

    const clearHold = () => {
        const t = touchBarRef.current;
        if (t && t.timer) clearTimeout(t.timer);
    };

    const handleBarTouchStart = (e, row) => {
        if (e.pointerType !== 'touch') return;
        clearHold();
        const rect = e.currentTarget.getBoundingClientRect();
        const anchor = e.currentTarget;
        touchBarRef.current = {
            rowId: row.id,
            x: e.clientX,
            y: e.clientY,
            held: false,
            rect,
            timer: setTimeout(() => {
                const t = touchBarRef.current;
                if (!t) return;
                t.held = true;
                // The hold wins: the drag that armed on pointerdown is aborted
                // and its preview dropped, so the bar goes back where it was.
                dragStateRef.current = null;
                setDragState(null);
                setDragPreview(null);
                dragPreviewRef.current = null;
                setPushedTaskPreview(null);
                pushedTaskPreviewRef.current = null;
                if (!readOnly && onStatusChange && row.type === 'subtask') {
                    // ⛔ ANCHORED TO THE ELEMENT, not to the event: this fires
                    // from a TIMER, long after the event object is any use.
                    const box = anchor.getBoundingClientRect();
                    setStatusPopup({
                        row,
                        x: box.left,
                        y: box.bottom + 5,
                        blockers: getBlockers(blockedTasksMap, row.originalId, row.projectId, false)
                    });
                }
            }, HOLD_MS)
        };
    };

    const handleBarTouchMove = (e) => {
        const t = touchBarRef.current;
        if (!t || t.held) return;
        // hypot, because a HOLD is about the finger being still.
        if (Math.hypot(e.clientX - t.x, e.clientY - t.y) > HOLD_SLOP) clearHold();
    };

    const handleBarTouchEnd = (e, row) => {
        const t = touchBarRef.current;
        touchBarRef.current = null;
        if (!t) return;
        clearHold();
        if (t.held) return;
        // deltaX alone, because a BAR only moves in x.
        if (Math.abs(e.clientX - t.x) > TAP_SLOP) return;

        // ⛔ A TAP MUST NOT COPY THE HOVER GUARD. It fires from the bar drag's
        // OWN release - setDragState ran at pointerdown - so anyDragActive is
        // true in every closure that can reach here and the guard would refuse
        // EVERY tap. A ref does not rescue it either: any "is a drag live" test
        // is true when a tap resolves, BECAUSE THE TAP IS THAT DRAG ENDING. Its
        // real conflicts are excluded above by `held` and by the slop.
        //
        // ⛔ And the card is INSTANT. A hover must prove it was deliberate; a tap
        // already is, and at 700ms it would land most of a second after the
        // finger lifted and read as a broken app.
        setTooltip({ content: buildTooltipContent(row), x: e.clientX, y: t.rect.top - 8 });
    };

    const handleBarMouseEnter = (e, row) => {
        if (anyDragActive || isPanningRef.current) return;
        // ⛔ A tap emits a SYNTHETIC mouseenter. Without this the tap arms the
        // 700ms timer as well, and a second card appears a beat after the finger
        // has gone.
        if (lastPointerTypeRef.current !== 'mouse') return;
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        // x is the CURSOR, not the bar's midpoint: a bar zoomed wider than the
        // viewport has its midpoint off-screen, and the clamp below then pinned
        // the card against the screen edge - nowhere near the pointer. y stays
        // the bar's top, so the card still sits above it and the arrow flips.
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltip({
                content: buildTooltipContent(row),
                x,
                y: rect.top - 8
            });
        }, 700);
    };

    const handleBarMouseLeave = () => {
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        // Small delay to allow user to move mouse to tooltip
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltip(null);
        }, 100);
    };

    // Dependency-line hover card: own lightweight fixed card, NOT the
    // interactive bar tooltip - pointer-events none so it can never sit in
    // the way of clicking the line
    const [linkTip, setLinkTip] = useState(null);
    const handleLinkHover = (payload) => {
        // Only a card being SHOWN is blocked. The arrow's onMouseLeave calls this
        // with null (DependencyArrows.js:176) and that must always get through,
        // or a card shown before the drag began would stick there afterwards.
        if (payload && (anyDragActive || isPanningRef.current)) return;
        setLinkTip(payload);
    };

    // Rows whose bars stay bright while a dependency chain is hovered.
    //
    // The chain is subtask-level, so a subtask is in it when its node key
    // (projectId#ref - the same identity the arrow renderer chains on) is in the
    // hovered set. Its main task and epic stay bright with it: rows are emitted
    // epic > task > subtask in order, so the nearest preceding row of each type
    // is the parent. Project Overview never dims - it spans everything, so
    // dimming it would say nothing.
    const chainRowIds = useMemo(() => {
        const nodes = linkTip?.nodes;
        if (!nodes || nodes.size === 0) return null;

        const bright = new Set();
        let lastEpicId = null;
        let lastTaskId = null;
        for (const row of rows) {
            if (row.type === 'project') {
                bright.add(row.id);
                continue;
            }
            if (row.type === 'status' || row.type === 'epic') lastEpicId = row.id;
            if (row.type === 'task') lastTaskId = row.id;
            if (row.type !== 'subtask') continue;
            const key = `${row.sourceTemplateId || row.projectId || ''}#${row.ref}`;
            if (!nodes.has(key)) continue;
            bright.add(row.id);
            if (lastTaskId) bright.add(lastTaskId);
            if (lastEpicId) bright.add(lastEpicId);
        }
        return bright;
    }, [linkTip, rows]);


    // Get display position (with drag preview or pushed task preview)
    const getDisplayPosition = (row) => {
        // Check if this row is being dragged
        if (dragPreview && dragPreview.rowId === row.id) {
            const startOffset = (dragPreview.start - dateRange.minDate) / (1000 * 60 * 60 * 24);
            const duration = Math.max((dragPreview.end - dragPreview.start) / (1000 * 60 * 60 * 24) + 1, 1);
            return {
                left: startOffset * pixelsPerDay,
                width: Math.max(duration * pixelsPerDay, 20)
            };
        }
        // Check if this row is being pushed by the FS cascade (plural now)
        if (pushedTaskPreview) {
            const pushed = pushedTaskPreview.find(p => p.rowId === row.id);
            if (pushed) {
                const startOffset = (pushed.start - dateRange.minDate) / (1000 * 60 * 60 * 24);
                const duration = Math.max((pushed.end - pushed.start) / (1000 * 60 * 60 * 24) + 1, 1);
                return {
                    left: startOffset * pixelsPerDay,
                    width: Math.max(duration * pixelsPerDay, 20),
                    isPushed: true // Flag to indicate this is a pushed preview
                };
            }
        }
        // Check for position override (persists until props confirm)
        const overrideKey = `${row.projectId || 'default'}:${row.originalId}`;
        const override = positionOverridesRef.current[overrideKey];
        if (override) {
            const start = new Date(override.start);
            const end = new Date(override.end);
            const startOffset = (start - dateRange.minDate) / (1000 * 60 * 60 * 24);
            const duration = Math.max((end - start) / (1000 * 60 * 60 * 24) + 1, 1);
            return {
                left: startOffset * pixelsPerDay,
                width: Math.max(duration * pixelsPerDay, 20)
            };
        }
        return getBarPosition(row);
    };

    // Today marker position
    const today = utcToday();
    const todayOffset = (today - dateRange.minDate) / (1000 * 60 * 60 * 24);
    const todayPosition = todayOffset * pixelsPerDay;

    // Kick-off marker position (use prop if provided, otherwise defaultKickOff)
    const effectiveKickOff = kickOffDate ? new Date(kickOffDate) : dateRange.defaultKickOff;

    // ⚠️ ONE COPY, read by the hover tooltip AND by the tap bubble a finger gets
    // (T409). Two spellings of the same label is how a control ends up with two
    // explanations, one of them stale.
    //
    // ⚠️ THE SECOND LINE NO LONGER SAYS "Drag to change" (T409). That was true of
    // the branch deleted with D12; without it a chart given onKickOffDateChange
    // and no onProjectDateShift draws the marker and drags nothing, so the label
    // would have been promising a gesture that does not exist. ClientView passes
    // both, so this is the unreachable arm - it is corrected rather than left to
    // be discovered by whoever makes it reachable.
    const kickOffMarkerLabel = onProjectDateShift
        ? <>Project Start: {effectiveKickOff?.toLocaleDateString('en-GB', { timeZone: 'UTC' })}<br />Drag to shift entire project (weekly increments)</>
        : <>Kick-off: {effectiveKickOff?.toLocaleDateString('en-GB', { timeZone: 'UTC' })}</>;
    const kickOffOffset = effectiveKickOff ? (effectiveKickOff - dateRange.minDate) / (1000 * 60 * 60 * 24) : null;
    const kickOffPosition = kickOffOffset !== null ? kickOffOffset * pixelsPerDay : null;

    // Fixed column widths for left panel (nameColWidth is state)
    const COLLAPSED_COL_WIDTH = 20;
    const startDateColWidth = 100;
    const durationColWidth = 80;
    // Effective widths: a collapsed group is a 20px strip. Everything
    // downstream (bars, grid, band spacer, drop maths) rides leftPanelWidth.
    // Resize keeps the RAW nameColWidth - folding must not lose the width.
    const effNameColWidth = nameColCollapsed ? COLLAPSED_COL_WIDTH : nameColWidth;
    const effDatesWidth = datesCollapsed ? COLLAPSED_COL_WIDTH : startDateColWidth + durationColWidth;
    // T475 - the Effort column. ⚠️ It MUST be part of leftPanelWidth: the bars,
    // the grid, the band spacer and the drop maths all ride that number, so a
    // column rendered without widening it would shift every bar left by its
    // width. Zero when hidden, so the client portal's panel is unchanged to the
    // pixel.
    // ⛔ No effort column width: the left-panel Effort column was vetoed by
    // Bill on sight (2026-09-01) - it rode in on a mis-recorded D12. The
    // Gantt shows effort in the BAR LABEL only (D15).
    const leftPanelWidth = effNameColWidth + effDatesWidth;
    const rawTimelineWidth = columns.length * columnWidth;
    // Ensure timeline is at least as wide as viewport minus left panel to prevent layout issues at low zoom
    const minTimelineWidth = Math.max(containerWidth - leftPanelWidth, 400);
    const timelineWidth = Math.max(rawTimelineWidth, minTimelineWidth);

    // FS engine context from current rows + canonical links. Shared by the
    // drag constraints and the typed-date clamp. Rebuilt on demand - rows and
    // links are stable within an interaction.
    const buildFsCtx = () => {
        const tasksByKey = {};
        const rowByKey = {};
        for (const r of rows) {
            if (r.type !== 'subtask') continue;
            if (!r.start && !r.end) continue;
            const k = `${r.ref}|${r.projectId || ''}`;
            tasksByKey[k] = {
                start: toDayNum(formatDateYMD(new Date(r.start || r.end))),
                end: toDayNum(formatDateYMD(new Date(r.end || r.start)))
            };
            rowByKey[k] = r;
        }
        const edges = [];
        for (const link of effectiveLinks) {
            const sRow = rows.find(r =>
                r.ref === getNumericRef(link.source) && r.type === 'subtask' &&
                (!link.sourceProjectId || (r.sourceTemplateId || r.projectId) === link.sourceProjectId));
            const tRow = rows.find(r =>
                r.ref === getNumericRef(link.target) && r.type === 'subtask' &&
                (!link.targetProjectId || (r.sourceTemplateId || r.projectId) === link.targetProjectId));
            if (!sRow || !tRow) continue;
            const sKey = `${sRow.ref}|${sRow.projectId || ''}`;
            const tKey = `${tRow.ref}|${tRow.projectId || ''}`;
            if (tasksByKey[sKey] && tasksByKey[tKey]) edges.push({ source: sKey, target: tKey });
        }
        return { ctx: { tasksByKey, edges }, rowByKey };
    };

    // Zoom anchoring: keep the date at the viewport centre fixed across a
    // zoom change instead of letting the chart fly off sideways
    const pendingScrollRef = useRef(null);
    // Left-edge position per scale, in days from the chart start. Restoring the
    // view on return needs a date, not a pixel offset: scrollLeft means a
    // different date once pixels-per-day changes.
    const scrollDaysByScaleRef = useRef({});
    const pendingScaleRef = useRef(null);
    const applyZoomAnchored = (newZoom) => {
        const el = scrollContainerRef.current;
        if (el) {
            const viewW = el.clientWidth;
            const timelineX = el.scrollLeft + viewW / 2 - leftPanelWidth;
            const ratio = newZoom / (zoomLevel || 1);
            pendingScrollRef.current = Math.max(0, timelineX * ratio + leftPanelWidth - viewW / 2);
            // The write lands a render later, in the layout effect below, and
            // would otherwise read as a user scroll and snap back - which D10
            // forbids. Stamping here covers it: milliseconds, well inside the
            // window.
            if (followMode) autoScrollUntilRef.current = performance.now() + 800;
        }
        setZoomLevel(newZoom);
    };

    // The vertical twin of pendingScrollRef. A reading-size change makes every
    // row taller, so scrollHeight is only correct AFTER the render - the write
    // has to wait for the same layout effect.
    const pendingScrollTopRef = useRef(null);

    // ===================================================================
    // The gestures (T366/T367). The hook computes the next scale; the
    // anchoring and the scroll write live HERE, because this is where
    // Follow mode's "that was me" window is stamped.
    // ===================================================================
    //
    // ⛔ THE TWO AXES ANCHOR DIFFERENTLY AND THE ASYMMETRY IS THE DESIGN.
    // Horizontal keeps the day under the POINTER fixed - the time axis is dense
    // with content wherever you point. Vertical anchors the TOP EDGE, because
    // that axis is a LIST: anchoring a pointer halfway down it drags whatever
    // you were reading off the top as the rows grow.
    //
    // ⚠️ applyZoomAnchored above keeps the viewport CENTRE and is not changed -
    // the slider, the -/+ buttons and FIT/ALL all still use it. A slider drag
    // has no pointer position over the chart to anchor to, so two anchoring
    // rules on one chart is deliberate rather than an inconsistency.
    const zoomRef = useRef(zoomLevel);
    zoomRef.current = zoomLevel;
    const uiScaleRef = useRef(uiScale);
    uiScaleRef.current = uiScale;
    // ⛔ The handlers live in a ref and the hook is given STABLE wrappers. They
    // read leftPanelWidth, zoomLevel and dateRange, all of which change every
    // few renders - passed directly, the gesture effect would tear down and
    // re-attach its listeners constantly, and a wheel gesture in flight would
    // lose its idle timer each time.
    const gestureFnsRef = useRef({});
    gestureFnsRef.current = {
        onZoom: (next, clientX) => {
            const el = scrollContainerRef.current;
            if (el) {
                const rect = el.getBoundingClientRect();
                // ⚠️ Measured from the TIMELINE's left edge, not the
                // container's: the left panel is sticky and does not scroll, so
                // including its width would anchor the wrong column.
                const offset = Math.max(0, clientX - rect.left - leftPanelWidth);
                const ratio = next / (zoomRef.current || 1);
                pendingScrollRef.current = anchoredScaleScroll(el.scrollLeft, offset, ratio);
                if (followMode) autoScrollUntilRef.current = performance.now() + 800;
            }
            setZoomLevel(next);
        },
        onUiScale: (next) => {
            const el = scrollContainerRef.current;
            if (el) {
                const ratio = next / (uiScaleRef.current || 1);
                // Offset 0: anchor the top edge. Whichever row you are reading
                // stays where it is.
                pendingScrollTopRef.current = anchoredScaleScroll(el.scrollTop, 0, ratio);
                if (followMode) autoScrollUntilRef.current = performance.now() + 800;
            }
            setUiScale(next);
        },
        // ⛔ A live gesture must not write localStorage every frame. The zoom
        // map's per-change effect is suppressed for the duration and both
        // scales are persisted once, at the end.
        onGestureStart: () => { gestureActiveRef.current = true; },
        onGestureEnd: () => {
            // Persist, THEN clear, and write no state after the clear - a
            // setZoomLevel issued after it would have its effect fire with the
            // suppression already off, which is the double-write.
            persistZoom();
            persistUiScale();
            gestureActiveRef.current = false;
        }
    };

    const onGestureZoom = useCallback((v, x) => gestureFnsRef.current.onZoom(v, x), []);
    const onGestureUiScale = useCallback((v) => gestureFnsRef.current.onUiScale(v), []);
    const onGestureStart = useCallback(() => gestureFnsRef.current.onGestureStart(), []);
    const onGestureEnd = useCallback(() => gestureFnsRef.current.onGestureEnd(), []);

    // ⚠️ NOT disabled while following. A SCALE change does not re-anchor (D10 -
    // the re-anchor effect is deliberately not keyed on zoomLevel), so a zoom
    // during Follow mode is a supported case the slider already exercises. Only
    // the SCROLL POSITION is Follow's to own, which is why the pan is disabled
    // above and this is not.
    useGanttGestures(scrollContainerRef, {
        disabled: false,
        pointersRef,
        blockedRef: gestureBlockedRef,
        zoomRef,
        uiScaleRef,
        onZoom: onGestureZoom,
        onUiScale: onGestureUiScale,
        onGestureStart,
        onGestureEnd
    });

    // Point the viewport at the anchor: the earliest open start date flush at
    // the left edge (D3, no padding day) and the group header above the first
    // open subtask at the top (D4, D27 - two different rows). Smooth, so a
    // closing task reads as the project moving on rather than a jump (D13).
    //
    // A callback rather than an effect body because the layout effect below
    // needs it too - see the auto-fit trap there.
    const applyFollowScroll = () => {
        if (!followMode || !followAnchor) return;
        const el = scrollContainerRef.current;
        if (!el || !dateRange?.minDate) return;

        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        const days = (followAnchor.time - dateRange.minDate) / 86400000;
        const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        const left = Math.min(maxLeft, Math.max(0, days * pxPerDay));
        // One row short of the group header, because the Project Overview row
        // is pinned under the date header while following - land the header ON
        // the anchor's row and the pinned row covers it.
        const top = Math.min(maxTop, Math.max(0, (followAnchor.topIndex - 1) * rowHeight));

        // Already there: nothing to re-issue.
        if (Math.abs(el.scrollLeft - left) <= 2 && Math.abs(el.scrollTop - top) <= 2) return;

        // Travel by hand, in FIXED time, assigning scrollLeft/scrollTop each
        // frame. Two faults are being avoided here, both found on preview
        // (Bill, 2026-08-23):
        //
        // 1. `scrollTo` was the only scroll in this file that did not assign
        //    directly. Every other date jump - TODAY, the footer slider,
        //    jump-to-date, FIT, the scale restore - assigns, because the
        //    container is overflow-x-hidden.
        // 2. `behavior: 'smooth'` has a browser-chosen duration that scales
        //    with DISTANCE, and a Gantt jump is tens of thousands of pixels:
        //    it crawled for many seconds. A fixed duration cannot, however far
        //    the chart has to travel.
        //
        // The window that tells Follow mode's own scrolling from the user's
        // covers the whole tween and a couple of frames - guessing 800ms while
        // a browser animation ran longer is what made the animation's own tail
        // events read as a user scroll and restart it, mid-crawl.
        if (followTweenRef.current) cancelAnimationFrame(followTweenRef.current);
        const fromLeft = el.scrollLeft;
        const fromTop = el.scrollTop;
        const started = performance.now();
        autoScrollUntilRef.current = started + FOLLOW_SCROLL_MS + 150;

        const step = () => {
            const t = Math.min(1, (performance.now() - started) / FOLLOW_SCROLL_MS);
            // easeInOutCubic - leaves and arrives gently, so a closing task
            // reads as the project moving on rather than a jump cut
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            el.scrollLeft = fromLeft + (left - fromLeft) * eased;
            el.scrollTop = fromTop + (top - fromTop) * eased;
            followTweenRef.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        followTweenRef.current = requestAnimationFrame(step);
    };

    useLayoutEffect(() => {
        if (pendingScrollRef.current !== null && scrollContainerRef.current) {
            const pending = pendingScrollRef.current;
            // Auto-fit's deferred write, while following, loses to the anchor.
            // The fix HAS to be here rather than in the auto-fit effect: that
            // effect calls handleFitAll, which only writes scrollLeft directly
            // when the zoom is unchanged - otherwise it defers to this very
            // write, so a re-apply up there would run first and be overwritten.
            const followWins = autoFitPendingRef.current && followMode && followAnchor;
            autoFitPendingRef.current = false;
            pendingScrollRef.current = null;
            if (followWins) {
                applyFollowScroll();   // post-fit zoomLevel is in scope HERE
            } else {
                scrollContainerRef.current.scrollLeft = pending;
            }
        }
        // The reading size's twin. Rows are only their new height after this
        // render, so scrollHeight is only correct now.
        if (pendingScrollTopRef.current !== null && scrollContainerRef.current) {
            const pendingTop = pendingScrollTopRef.current;
            pendingScrollTopRef.current = null;
            scrollContainerRef.current.scrollTop = pendingTop;
        }
        // Arriving at a scale we have been on before: put the same date back at
        // the left edge, using that scale's own pixels-per-day. Scales never
        // visited fall through to the auto-fit effect instead.
        if (pendingScaleRef.current === currentScale && scrollContainerRef.current) {
            const days = scrollDaysByScaleRef.current[currentScale];
            pendingScaleRef.current = null;
            if (days != null) {
                const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
                scrollContainerRef.current.scrollLeft = Math.max(0, days * pxPerDay);
            }
        }
    // ⛔ uiScale IS IN HERE, AND WITHOUT IT THE VERTICAL ANCHOR SILENTLY DOES
    // NOTHING (T387, 2026-08-30). pendingScrollTopRef is written by the vertical
    // pinch (above), the V.Zoom reset and ALL's vertical fit - all three of which
    // change uiScale WITHOUT changing zoomLevel or currentScale. Missing from the
    // deps, the write sits in the ref until some later horizontal zoom applies it
    // stale, against a different row height. TemplateGantt.js has always had it.
    //
    // The other two branches are safe to run on a uiScale change: each is guarded
    // by a null/identity check and no-ops when nothing is pending.
    }, [zoomLevel, currentScale, uiScale]);

    // Re-anchor when the data moves: closing a task rebuilds rows, which
    // rebuilds followAnchor, which lands here. Deliberately NOT keyed on
    // zoomLevel - a manual zoom must not drag the view back (D10); the next
    // closure re-anchors.
    useEffect(() => {
        applyFollowScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followMode, followAnchor, dateRange, currentScale]);

    // Nothing should still be travelling once follow is off, or once the chart
    // is unmounted - the tween holds the scroll element.
    useEffect(() => () => {
        if (followTweenRef.current) {
            cancelAnimationFrame(followTweenRef.current);
            followTweenRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (followMode) return;
        if (followTweenRef.current) {
            cancelAnimationFrame(followTweenRef.current);
            followTweenRef.current = null;
        }
    }, [followMode]);

    // Put the view back where it was before follow mode took over (Bill,
    // 2026-08-23). Deliberately an effect and not part of exitFollow: it has to
    // run after the restored scale has actually been applied, or the days are
    // converted at Day-scale pixels-per-day and the chart lands on the wrong
    // date. Runs after the tween-cancel above, so nothing is still travelling.
    useEffect(() => {
        if (followMode) return;
        const restore = pendingFollowRestoreRef.current;
        if (!restore) return;
        pendingFollowRestoreRef.current = null;
        const el = scrollContainerRef.current;
        if (!el) return;
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        el.scrollLeft = Math.max(0, restore.days * pxPerDay);
        el.scrollTop = Math.max(0, restore.top);
    }, [followMode, currentScale, zoomLevel, scrollContainerRef]);

    // Snap-back (D9). The tween writes scroll positions of its own, so "was
    // that the user?" is a time window rather than a boolean. Debounced, so a
    // snap never fights a finger mid-drag.
    useEffect(() => {
        if (!followMode) return undefined;
        const el = scrollContainerRef.current;
        if (!el) return undefined;
        let timer = null;
        const onScroll = () => {
            if (performance.now() <= autoScrollUntilRef.current) return;
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => { timer = null; applyFollowScroll(); }, 150);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [followMode, followAnchor, dateRange, currentScale, zoomLevel]);

    // Changing scale is three steps, not one: record where the outgoing scale
    // was left - in DAYS, because a pixel offset means a different date once
    // pixels-per-day changes - name the incoming scale, then switch. The layout
    // effect above reads both to put the same date back at the left edge.
    //
    // The scale buttons did this inline. Follow mode's activation and exit need
    // it too, and a bare setCurrentScale from either would leave the chart at
    // the right scale showing an arbitrary date.
    const switchScale = (next) => {
        const el = scrollContainerRef.current;
        if (el) {
            const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
            if (pxPerDay > 0) {
                scrollDaysByScaleRef.current[currentScale] = el.scrollLeft / pxPerDay;
            }
        }
        pendingScaleRef.current = next;
        setCurrentScale(next);
    };

    // Enter Follow mode: full screen, hide completed, Day scale (D6), and the
    // three remembered view settings reset (D22) - a parent someone collapsed
    // weeks ago would hide its open subtasks from the anchor and point the TV
    // at the wrong work, with nothing on screen saying so. Everything replaced
    // is stashed first so exiting can put it back.
    const enterFollow = () => {
        // Where the chart was looking, so exiting can put it back. The
        // horizontal position is kept in DAYS, not pixels: follow mode forces
        // Day scale, and a pixel offset means a different date at a different
        // pixels-per-day.
        const el = scrollContainerRef.current;
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1);
        localStorage.setItem('ganttFollowPrev', JSON.stringify({
            hideCompleted,
            scale: currentScale,
            expandedItems,
            laneFilter,
            viewMode,
            scrollDays: el && pxPerDay > 0 ? el.scrollLeft / pxPerDay : 0,
            scrollTop: el ? el.scrollTop : 0
        }));
        setHideCompleted(true);
        switchScale('day');
        setExpandedItems({});     // absent key means expanded, so this expands all
        setLaneFilter('all');
        setViewMode(null);        // the grouped tree - flat views have no headers
        if (onToggleFullScreen) onToggleFullScreen(true);
        setFollowMode(true);
    };

    // Leave it: put back everything activation replaced, and drop full screen
    // with it. A missing, unparseable or invalid stash restores nothing rather
    // than restoring half of it.
    const exitFollow = () => {
        setFollowMode(false);
        const prev = readFollowPrev();
        if (prev) {
            setHideCompleted(prev.hideCompleted);
            switchScale(prev.scale);
            setExpandedItems(prev.expandedItems);
            setLaneFilter(prev.laneFilter);
            setViewMode(prev.viewMode);
            // Applied by the effect below rather than here: the scale it must
            // be measured against has only just been asked for, so
            // pixels-per-day is still the follow-mode one at this point.
            pendingFollowRestoreRef.current = { days: prev.scrollDays, top: prev.scrollTop };
        }
        localStorage.removeItem('ganttFollowPrev');
        if (onToggleFullScreen) onToggleFullScreen(false);
    };

    // Jump to a picked date - lands it on the LEFT edge of the window, the same
    // edge the footer slider reports. The picker's min/max are the project range
    // so the calendar greys out anything off the chart; the scroll is clamped
    // too, since the last few days cannot reach the left edge.
    const handleJumpToDate = (e) => {
        const value = e.target.value;
        setJumpDate(value);
        const scrollContainer = scrollContainerRef.current;
        if (!value || !scrollContainer || !dateRange?.minDate) return;
        const days = Math.round((new Date(`${value}T00:00:00Z`) - dateRange.minDate) / 86400000);
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        const target = Math.max(0, Math.min(maxScroll, days * pixelsPerDay));
        scrollContainer.scrollLeft = target;
        setScrollPercent(maxScroll > 0 ? (target / maxScroll) * 100 : 0);
    };

    // Nothing left open anywhere. Measured on the raw subtasks prop, never on
    // rows: while following, hide-completed is locked on, so by the time this
    // is true there are no rows left to measure.
    const projectComplete = subtasks.length > 0 && subtasks.every(st => st.isClosed === true);

    // The end state on the TV (D14).
    //
    // FIXED, not absolute, and the chart root is deliberately NOT given
    // `relative` to anchor an absolute one: it would become the containing
    // block for every absolutely-positioned descendant that has no nearer
    // positioned ancestor, in both portals, and no test here can see geometry.
    // Follow mode is always full screen, so the viewport is the correct frame
    // anyway.
    //
    // It carries its own exit because it covers the toolbar - the play button
    // underneath it cannot be clicked.
    const completionBanner = (
        <div className="fixed inset-0 z-[110] flex flex-col items-center justify-center gap-6 bg-white/80">
            <div className="text-5xl font-bold text-green-600">Project Completed!</div>
            <button
                onClick={exitFollow}
                className="px-4 h-9 flex items-center justify-center rounded bg-green-600 text-white text-sm font-medium shadow-sm shadow-gray-500/60 hover:bg-green-700"
            >
                Stop following
            </button>
        </div>
    );

    if (rows.length === 0) {
        // While following, a bare message is a trap: Esc is inert (D11) and
        // full screen covers the portal's own navigation, so the play button is
        // the only way out and this branch has no toolbar to hold one. Reached
        // by following one project and opening another with no tasks yet - the
        // follow flag is shared, the project is not.
        if (followMode) {
            if (projectComplete) return completionBanner;
            return (
                <div className="h-full flex flex-col items-center justify-center gap-4 text-gray-500">
                    <span>No tasks to display</span>
                    <button
                        onClick={exitFollow}
                        className="px-4 h-9 flex items-center justify-center rounded bg-green-600 text-white text-sm font-medium shadow-sm shadow-gray-500/60 hover:bg-green-700"
                    >
                        Stop following
                    </button>
                </div>
            );
        }
        return (
            <div className="h-full flex items-center justify-center text-gray-500">
                No tasks to display
            </div>
        );
    }

    // Check if a status is "Needs Info" (status can be numeric ID from DynamoDB)
    const isNeedsInfo = (status) => String(status || '').toLowerCase().includes('needs info');

    return (
        <div ref={containerRef} className="h-full flex flex-col bg-white rounded-lg shadow overflow-hidden">
            {/* The follow-mode ring (D16). A FIXED, empty, click-through leaf -
                not a class on the root above, which now gains nothing at all.
                Two reasons, and the first was found by rendering it:

                an inset box-shadow paints under the element's OWN children, and
                this root's toolbar, scroller and footer cover every pixel of
                it, so `ring-4 ring-inset` on the root was invisible in a real
                browser while passing a class assertion in jsdom. An empty leaf
                has nothing to paint over it.

                And a ring on the root would want `relative` sooner or later;
                this way the root keeps the exact class list it has always had.
                Follow mode is always full screen (D6, D11), so the viewport IS
                the chart. z-[105] sits above the full-screen wrapper and below
                the completion banner. */}
            {followMode && (
                <div className="fixed inset-0 z-[105] pointer-events-none ring-4 ring-inset ring-green-500" />
            )}
            {followMode && projectComplete && completionBanner}
            {/* Locked-control message (D7, D23). Fixed rather than tucked into
                the nav group's popup slot: that group is hidden at month scale,
                and anchoring it there would mean making an existing element a
                positioned ancestor. Same black card, same 4s life. */}
            {lockedMsg && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[120] whitespace-nowrap rounded bg-gray-800 px-3 py-2 text-sm leading-4 text-white shadow-lg">
                    {lockedMsg}
                </div>
            )}
            {/* Zoom controls */}
            <div className="flex items-center px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex-1 flex items-center gap-4">

                    {/* Expand/Collapse buttons */}
                    <span className="text-sm font-medium text-gray-700">Expand/Collapse:</span>
                    <div className="flex gap-1">
                        <Tooltip text="Expand/Collapse all Epics (Status groups)">
                            <button
                                onClick={() => toggleAllByType('status')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Epics
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse all Main Tasks">
                            <button
                                onClick={() => toggleAllByType('task')}
                                disabled={viewMode === 'st'}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Main Tasks
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse T.M.E. Tasks">
                            <button
                                onClick={() => toggleAllByType('task', 'tme')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                T.M.E. Tasks
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse Client Tasks">
                            <button
                                onClick={() => toggleAllByType('task', 'team')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Client Tasks
                            </button>
                        </Tooltip>
                    </div>

                    {/* Flat views + lane filter - Planning/GANTT-FLAT-VIEWS-2026-08-21.md.
                        A view button clicked while active exits to the tree; the lane
                        filter composes with every view, the tree included. */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <span className="text-sm font-medium text-gray-700">View:</span>
                    <div className="flex gap-1">
                        {[['mt', 'MT'], ['mtst', 'MT+ST'], ['st', 'ST']].map(([mode, label]) => (
                            <button
                                key={mode}
                                onClick={() => setViewMode(viewMode === mode ? null : mode)}
                                aria-pressed={viewMode === mode}
                                className={`px-2 py-1 text-sm leading-4 font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    viewMode === mode ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <span className="text-sm font-medium text-gray-700">Lane:</span>
                    <div className="flex gap-1">
                        {[['all', 'All'], ['tme', 'TME'], ['team', 'Client']].map(([lane, label]) => (
                            <button
                                key={lane}
                                onClick={() => setLaneFilter(lane)}
                                aria-pressed={laneFilter === lane}
                                className={`px-2 py-1 text-sm leading-4 font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    laneFilter === lane ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Hide completed main tasks AND their closed subtasks */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <Tooltip text={hideCompleted ? 'Show completed tasks and subtasks' : 'Hide completed tasks and subtasks'}>
                        <button
                            onClick={() => {
                                // Locked on while following (D7) - the TV shows
                                // outstanding work, and this is the switch that
                                // would undo that.
                                if (followMode) { setLockedMsg('Hide completed is locked on in Follow mode'); return; }
                                setHideCompleted(!hideCompleted);
                            }}
                            className={`px-3 py-1 text-sm leading-4 font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                hideCompleted
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                        >
                            Hide completed
                        </button>
                    </Tooltip>

                    {/* Date navigation. The STEP buttons are day and week only: a
                        week step on month scale would be a fraction of a column,
                        and a day step invisible.

                        `Now` is NOT gated with them (Bill, 2026-08-30). That
                        reason is about STEPPING, and Now is a centring action, so
                        it does not reach it - and since the TODAY button that used
                        to cover month scale from the right-hand group is gone,
                        gating Now here would leave month scale with no way to
                        centre today at all.

                        The `relative` wrapper is therefore ungated too: it is the
                        positioning parent of the out-of-range warning below, which
                        Now can now raise at month scale. */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <div className="relative flex items-center gap-1">
                        {currentScale !== 'month' && (
                            <button
                                onClick={() => scrollByDays(-7)}
                                onMouseEnter={(e) => showNavTip(e, 'Back one week')}
                                onMouseLeave={hideNavTip}
                                disabled={navEdges.atStart}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                <FaAngleDoubleLeft />
                            </button>
                        )}
                        {currentScale === 'day' && (
                            <button
                                onClick={() => scrollByDays(-1)}
                                onMouseEnter={(e) => showNavTip(e, 'Back one day')}
                                onMouseLeave={hideNavTip}
                                disabled={navEdges.atStart}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                <FaAngleLeft />
                            </button>
                        )}
                        <button
                            onClick={handleToday}
                            onMouseEnter={(e) => showNavTip(e, 'Centre today in the timeline')}
                            onMouseLeave={hideNavTip}
                            className="px-3 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                        >
                            Now
                        </button>
                        {currentScale === 'day' && (
                            <button
                                onClick={() => scrollByDays(1)}
                                onMouseEnter={(e) => showNavTip(e, 'Forward one day')}
                                onMouseLeave={hideNavTip}
                                disabled={navEdges.atEnd}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                <FaAngleRight />
                            </button>
                        )}
                        {currentScale !== 'month' && (
                            <button
                                onClick={() => scrollByDays(7)}
                                onMouseEnter={(e) => showNavTip(e, 'Forward one week')}
                                onMouseLeave={hideNavTip}
                                disabled={navEdges.atEnd}
                                className="px-2 py-1 text-sm leading-4 font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                <FaAngleDoubleRight />
                            </button>
                        )}
                        {todayOutsideRange && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-sm leading-4 text-white shadow-lg">
                                Today's date not in project range
                            </div>
                        )}
                    </div>
                </div>
                {/* Colour key - uses actual task statuses */}
                <div className="flex items-center gap-3">
                    <span className="text-sm leading-4 font-semibold text-gray-500">Key:</span>
                    {taskStatuses
                        .filter(s => !s.is_archived)
                        .sort((a, b) => a.order - b.order)
                        .map(status => (
                            <div key={status.id} className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: getStatusColour(status.name, status.isClosed) }} />
                                <span className="text-sm leading-4 text-gray-600">{status.name}</span>
                            </div>
                        ))
                    }
                    <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
                        <div className="w-3 h-3 rounded-sm bg-gray-100" style={{ borderLeft: '3px solid #3b82f6' }} />
                        <span className="text-sm leading-4 text-gray-600">Client Task</span>
                    </div>
                </div>

                <div className="flex-1 flex items-center gap-2 justify-end">
                    <span className="text-sm font-medium text-gray-700">Scale:</span>
                    <div className="flex gap-1">
                        {['day', 'week', 'month'].map((scale) => (
                            <button
                                key={scale}
                                onClick={() => switchScale(scale)}
                                className={`px-3 py-1 text-sm leading-4 font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    currentScale === scale
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {scale.charAt(0).toUpperCase() + scale.slice(1)}
                            </button>
                        ))}
                    </div>
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    {/* H.Zoom, because there are two zooms on this bar now and
                        the reading size is the other one (T377). */}
                    <span className="text-sm font-medium text-gray-700">H.Zoom:</span>
                    <button
                        onClick={() => applyZoomAnchored(Math.max(0.25, (zoomLevel || 1) - 0.25))}
                        className="w-7 h-7 flex items-center justify-center text-base leading-5 font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                    >
                        -
                    </button>
                    <ZoomSlider zoomLevel={zoomLevel} onCommit={applyZoomAnchored} />
                    <button
                        onClick={() => applyZoomAnchored(Math.min(8, (zoomLevel || 1) + 0.25))}
                        className="w-7 h-7 flex items-center justify-center text-base leading-5 font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                    >
                        +
                    </button>
                    <span className="text-xs text-gray-600 w-12 text-center">
                        {Math.round((zoomLevel || 1) * 100)}%
                    </span>
                    {/* V.Zoom - the reading size, after H.Zoom's own percentage
                        and behind a divider, so the horizontal group stays whole.

                        ⛔ NO SLIDER AND NO +/- BUTTONS, and the asymmetry IS the
                        ruling. The reading size is driven by ctrl+wheel and an
                        upright pinch; a third control on this bar is the "three
                        zooms on one screen" that was refused. It is a READOUT
                        THAT HAPPENS TO BE CLICKABLE.

                        ⚠️ It reads 30%-250%, not 20%-800%: uiScale's clamp is
                        not the time axis's, so the two figures side by side will
                        not look like the same scale - because they are not. */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <span className="text-sm font-medium text-gray-700">V.Zoom:</span>
                    <Tooltip text="Reading size - ctrl+wheel or pinch upright. Click to reset to 100%">
                        <button
                            onClick={() => {
                                // ⛔ A click already at 100% returns early and
                                // writes NOTHING: the default is the absence of
                                // the stored key, and creating it would be a
                                // write nobody asked for.
                                if (uiScale === UI_SCALE_DEFAULT) return;
                                const el = scrollContainerRef.current;
                                if (el) {
                                    // ⛔ ANCHOR THE TOP EDGE, the same rule the
                                    // wheel uses. A reset from 250% otherwise
                                    // drops the reader onto empty chart - "it
                                    // zooms to an empty chart" by another door.
                                    const ratio = UI_SCALE_DEFAULT / (uiScale || 1);
                                    pendingScrollTopRef.current =
                                        anchoredScaleScroll(el.scrollTop, 0, ratio);
                                    // ⚠️ Follow mode allows this - a SCALE change
                                    // does not re-anchor (D10) - but the scroll
                                    // write still has to be stamped, or the
                                    // 150ms snap-back reads it as the user and
                                    // fights it.
                                    if (followMode) autoScrollUntilRef.current = performance.now() + 800;
                                }
                                setUiScale(UI_SCALE_DEFAULT);
                                // Durable straight away: this is a click, not a
                                // continuous gesture, so there is no end event
                                // to wait for.
                                persistUiScale(UI_SCALE_DEFAULT);
                            }}
                            className="px-2 h-7 flex items-center justify-center text-xs text-gray-600 rounded hover:bg-gray-200"
                        >
                            {Math.round(snapUiScale(uiScale) * 100)}%
                        </button>
                    </Tooltip>
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    {/* TODAY was here (Bill, 2026-08-30): removed, because «Now»
                        in the nav group does the same job and now renders at every
                        scale. It only lived in this group because Now used to be
                        hidden at month scale - remove that gate and the second
                        button is duplication, and this row has no width to spare.
                        organise-app went the other way (T359, FIT ALL TODAY); TME
                        does not. */}
                    <Tooltip text="Fit the subtasks in the current date window">
                        <button
                            onClick={handleFit}
                            className="px-2 h-7 flex items-center justify-center text-[13px] font-semibold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                        >
                            FIT
                        </button>
                    </Tooltip>
                    <Tooltip text="Fit the entire project">
                        <button
                            onClick={handleFitAll}
                            className="px-2 h-7 flex items-center justify-center text-[13px] font-semibold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                        >
                            ALL
                        </button>
                    </Tooltip>
                    {onToggleFullScreen && (
                        <Tooltip text={fullScreen ? 'Exit full screen' : 'Full screen'}>
                            <button
                                onClick={() => {
                                    // Locked while following (D23). Unguarded it
                                    // drops full screen with follow still
                                    // running: an embedded chart scrolling
                                    // itself, and a viewport-wide banner over
                                    // the whole portal. Same exit as Esc (D11),
                                    // by another route.
                                    if (followMode) { setLockedMsg('Full screen is locked on in Follow mode'); return; }
                                    onToggleFullScreen(!fullScreen);
                                }}
                                className="px-2 h-7 flex items-center justify-center rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                                aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
                            >
                                {fullScreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
                            </button>
                        </Tooltip>
                    )}
                    {/* Follow mode - after full screen (D15), and inside the same
                        gate, so the template editor gets neither. */}
                    {onToggleFullScreen && (
                        <Tooltip text={followMode ? 'Stop following' : 'Follow mode'}>
                            <button
                                onClick={() => (followMode ? exitFollow() : enterFollow())}
                                className={`px-2 h-7 flex items-center justify-center rounded shadow-sm shadow-gray-500/60 ${
                                    followMode
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                                aria-label={followMode ? 'Stop following' : 'Follow mode'}
                            >
                                <FaPlay size={12} />
                            </button>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Main content */}
            {/* Vertical scrollbar stays, horizontal is gone: the horizontal bar
                duplicated the footer date slider and they fought. Gecko has no
                per-axis scrollbar control, so the X axis is hidden on the
                container itself with overflow-x-hidden. Date navigation is the
                footer slider, FIT/Today and the nav buttons - all of which set
                scrollLeft directly, which still works on a hidden axis. Sideways
                wheel/trackpad does not.

                Deliberately NOT .gantt-scroll: that class hides scrollbars on
                both axes and would take the vertical one with it. */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto overflow-x-hidden"
                // Which device is in use RIGHT NOW, for the hover-card guard.
                // ⛔ Capture phase and BOTH events: pointerdown alone would leave
                // 'touch' behind after a tap, and a later mouse HOVER produces no
                // press at all - so on an iPad with a trackpad the card would
                // stay dead until the next click.
                onPointerDownCapture={(e) => { lastPointerTypeRef.current = e.pointerType; }}
                onPointerMoveCapture={(e) => { lastPointerTypeRef.current = e.pointerType; }}
            >
                {/* ⛔ --gantt-scale GOES HERE AND NOWHERE HIGHER (T371).
                    On the chart root it would scale the toolbar and the footer
                    too - the zoom slider, the nav buttons, the V.Zoom readout -
                    so at 2.5x the controls would eat the viewport and there
                    would be NO WAY TO ZOOM BACK OUT except a reload.

                    ⚠️ calc() and a custom property, never `em`: em compounds
                    through the label's nesting. And no `var(--gantt-scale, 1)`
                    fallback anywhere - an element outside this subtree should be
                    FOUND, not quietly rendered at 1x. A test asserts the absence.

                    The portalled overlays (tooltips, popups, the follow ring and
                    banner) are all outside this element already, which is what
                    keeps an overlay from covering the thing it describes. */}
                <div className="relative" style={{ minWidth: leftPanelWidth + timelineWidth, '--gantt-scale': uiScale }}>
                    {/* Header rows */}
                    {/* z-40, NOT z-20 (2026-08-24). The header was always sticky
                        and always in the right place - it was being PAINTED OVER.
                        Each row's left panel is `sticky left-0 z-20`, the same z as
                        this was, and rows come later in the DOM, so every row panel
                        scrolling past covered the Task Name / Start / Duration
                        headings and the whole header read as unlocked.

                        z-40 rather than z-30 purely for headroom: this header's own
                        inner panels are z-30, and although they sit inside its
                        stacking context and cannot clash, a root and a child both
                        reading 30 invites someone to "tidy" one of them later.
                        Nothing needs to paint above it - the bar tooltip and the
                        popups are `fixed` at 9998/9999 and render as siblings of the
                        scroll container, not inside this. Found in organise-app,
                        which carries the same chart. */}
                    <div ref={headerRef} className="sticky top-0 z-40">
                        {/* Month row (for day and week scales) */}
                        {/* The month BAND. gray-200 over the strip's gray-100 read as
                            labels floating over the dates. Dark band in the site's popup
                            gray, h-6 EXPLICITLY - the label spans are absolute, so
                            nothing in-flow gives the row height without it. */}
                        {currentScale !== 'month' && monthSpans.length > 0 && (
                            <div className="flex h-6 bg-gray-500 border-b border-gray-600">
                                {/* Left panel spacer for month row */}
                                <div className="sticky left-0 bg-gray-500 z-30 border-r border-gray-600" style={{ width: leftPanelWidth }}>
                                    <div className="px-3 py-1 font-medium text-sm text-gray-700">
                                        {/* Empty - just spacing */}
                                    </div>
                                    {/* Task-name/dates divider through the band, in the band's
                                        own border colour - light-on-dark reads as a scratch
                                        (organise 93e7e8c, re-decided in 2026-08-21). Hidden
                                        with the handle when the name column is folded. */}
                                    {!nameColCollapsed && (
                                        <div className="absolute top-0 bottom-0 w-px bg-gray-600" style={{ left: effNameColWidth }} />
                                    )}
                                </div>
                                {/* Month spans. Absolutely positioned off the same
                                    idx * columnWidth arithmetic as the day row, the
                                    grid layer and getBarPosition - see the comment
                                    on the day row below. */}
                                <div className="relative self-stretch" style={{ width: timelineWidth }}>
                                    {monthSpans.map((span, idx) => (
                                        <div
                                            key={idx}
                                            className={`absolute top-0 bottom-0 flex items-center justify-center text-xs font-semibold border-r border-gray-500 text-white ${idx % 2 === 0 ? 'bg-gray-500' : 'bg-[#848b98]'}`}
                                            style={{
                                                left: span.startIndex * columnWidth,
                                                width: span.count * columnWidth
                                            }}
                                        >
                                            {span.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Day/Week/Month labels row */}
                        <div className="flex bg-gray-100 border-b border-gray-300">
                            {/* Left panel headers */}
                            <div className="sticky left-0 flex bg-gray-100 z-30 border-r border-gray-400">
                                {nameColCollapsed ? (
                                    <div className="flex items-center justify-center" style={{ width: COLLAPSED_COL_WIDTH }}>
                                        <button type="button" aria-label="Expand task name column" onClick={() => setNameColCollapsed(false)} className={COL_CHEVRON_CLASS}>›</button>
                                    </div>
                                ) : (
                                    <div
                                        className="relative px-3 py-2 font-medium text-sm text-gray-700"
                                        style={{ width: nameColWidth }}
                                    >
                                        <span className="flex items-center gap-2">
                                            <button type="button" aria-label="Collapse task name column" onClick={() => setNameColCollapsed(true)} className={COL_CHEVRON_CLASS}>‹</button>
                                            <span>Task Name</span>
                                        </span>
                                        {/* Task-name/dates divider (organise 93e7e8c ported,
                                            Bill re-decided 2026-08-21) */}
                                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-400" />
                                        {/* Resize handle - 6px, straddling the divider line */}
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-1.5 translate-x-1/2 cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                            style={{ zIndex: 50 }}
                                            onPointerDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // ⛔ Capture, so a release outside
                                                // the window still delivers its
                                                // pointerup - without it the column
                                                // follows the cursor back in until
                                                // the next click. Guarded: an
                                                // already-inactive pointer throws.
                                                try {
                                                    e.currentTarget.setPointerCapture(e.pointerId);
                                                } catch (err) { /* pointer already gone */ }
                                                setColResizing({ startX: e.clientX, startWidth: nameColWidth });
                                            }}
                                        />
                                    </div>
                                )}
                                {datesCollapsed ? (
                                    <div className="flex items-center justify-center" style={{ width: COLLAPSED_COL_WIDTH }}>
                                        <button type="button" aria-label="Expand dates" onClick={() => setDatesCollapsed(false)} className={COL_CHEVRON_CLASS}>›</button>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="flex items-center px-2 py-2 font-medium text-sm text-gray-700 border-r border-gray-200"
                                            style={{ width: startDateColWidth }}
                                        >
                                            {/* In flow, not absolute - overlaid it collided with
                                                the centred label at every offset tried */}
                                            <button type="button" aria-label="Collapse dates" onClick={() => setDatesCollapsed(true)} className={COL_CHEVRON_CLASS}>‹</button>
                                            <span className="flex-1 text-center">Start</span>
                                        </div>
                                        <div
                                            className="px-3 py-2 font-medium text-sm text-gray-700 text-center"
                                            style={{ width: durationColWidth }}
                                        >
                                            Duration
                                        </div>
                                    </>
                                )}
                                {/* ⛔ NO EFFORT COLUMN IN THE LEFT PANEL. T475
                                    built one off a mis-recorded D12 ("All 6");
                                    Bill had said NO in the original chat and
                                    vetoed it on sight (2026-09-01). Effort in
                                    the Gantt is the BAR LABEL (D15), nothing
                                    else. Do not reinstate. */}
                            </div>

                            {/* Timeline column headers */}
                            {/* Day/week/month columns.
                                ABSOLUTELY POSITIONED, NOT A FLEX ROW. columnWidth is
                                baseWidth * zoomLevel and FIT sets a fractional zoom, so
                                a flex row of `width: columnWidth` boxes has every column
                                rounded to device pixels and the error ACCUMULATES across
                                the row. Bars (getBarPosition) and the grid layer both use
                                exact idx * columnWidth, so a rounded header drifts away
                                from them the further right you scroll - which reads as a
                                uniform offset because only a couple of columns are ever
                                on screen at once. Every strip now uses the same exact
                                arithmetic, so they cannot disagree. */}
                            <div className="relative self-stretch" style={{ width: timelineWidth }}>
                                {columns.map((col, idx) => {
                                    const isSaturday = col.date && col.date.getUTCDay() === 6;
                                    const isSunday = col.date && col.date.getUTCDay() === 0;
                                    return (
                                        <div
                                            key={idx}
                                            className={`absolute top-0 bottom-0 flex items-center justify-center text-xs font-medium px-1 py-2 text-center ${
                                                col.isToday ? 'bg-orange-100 text-orange-700' :
                                                col.isWeekend ? 'bg-gray-200 text-gray-500' : 'text-gray-600'
                                            } ${
                                                isSaturday ? 'border-l border-l-gray-200' : ''
                                            } ${
                                                isSunday ? 'border-r border-r-gray-200' : 'border-r border-gray-100'
                                            }`}
                                            style={{ left: idx * columnWidth, width: columnWidth }}
                                        >
                                            {/* Hide day numbers at 50% zoom or lower to prevent squishing */}
                                            {!(currentScale === 'day' && zoomLevel !== null && zoomLevel <= 0.5) && col.label}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Column grid - ONE layer for the entire chart, behind every row.
                        Was a div per day PER ROW: ~395 columns x 117 rows put ~46,000
                        nodes in the scroller and every horizontal move had to repaint
                        them. Drawn once it is ~395, and it uses the same exact
                        idx * columnWidth as the header strips and getBarPosition, so it
                        cannot drift from either.

                        z-1 puts it above the rows' opaque backgrounds but below the bars
                        (z-10) and the dependency arrows (z-5). pointer-events-none so it
                        never intercepts a row click. */}
                    <div
                        className="absolute pointer-events-none"
                        style={{
                            left: leftPanelWidth,
                            top: headerHeight || (currentScale !== 'month' ? 64 : 36),
                            width: timelineWidth,
                            height: rows.length * rowHeight,
                            zIndex: 1
                        }}
                    >
                        {columns.map((col, idx) => {
                            const isSaturday = col.date && col.date.getUTCDay() === 6;
                            const isSunday = col.date && col.date.getUTCDay() === 0;
                            return (
                                <div
                                    key={idx}
                                    className={`absolute top-0 bottom-0 ${
                                        col.isWeekend ? 'bg-gray-200/50' : ''
                                    } ${
                                        isSaturday ? 'border-l border-l-gray-200' : ''
                                    } ${
                                        isSunday ? 'border-r border-r-gray-200' : 'border-r border-gray-100'
                                    }`}
                                    style={{ left: idx * columnWidth, width: columnWidth }}
                                />
                            );
                        })}

                        {/* The 1st of each month, dashed - ported from organise
                            GanttGrid.js (Bill, 2026-08-21). Drawn off monthSpans, not by
                            testing each column's date, so it lands on the SAME pixel as
                            the month band's edge above at every scale. First span
                            skipped: its start is the chart's left edge, where a rule
                            says nothing and doubles the border already there. */}
                        {currentScale !== 'month' && monthSpans.slice(1).map((span) => (
                            <div
                                key={`month-rule-${span.startIndex}`}
                                className="absolute top-0 bottom-0 border-l border-dashed border-gray-500"
                                style={{ left: span.startIndex * columnWidth }}
                            />
                        ))}
                    </div>

                    {/* Data rows */}
                    {rows.map((row, rowIdx) => {
                        const pos = getDisplayPosition(row);
                        const isDragging = dragState?.rowId === row.id;
                        // Check if this is a client/Team swimlane task (for blue colouring)
                        const isClientTask = row.type === 'task' && row.isSummary && teamSwimlaneId && row.swimlaneId === teamSwimlaneId;
                        // Check if this is an add-in task (use add-in colour)
                        const isAddinTask = row._isAddin === true;
                        const barColour = isAddinTask ? row._addinColour
                            : row.type === 'project' ? PROJECT_COLOUR
                            : (row.type === 'status' || row.type === 'epic') ? STATUS_COLOUR
                            : (row.isSummary ? (isClientTask ? CLIENT_PARENT_COLOUR : PARENT_COLOUR) : getStatusColour(row.status, row.isClosed, row.isMeeting));
                        const indentPx = row.level * 20;

                        // Row background colours (blue for client/Team tasks, green for others)
                        const rowBgClass = row.type === 'project'
                            ? 'bg-slate-300'
                            : (row.type === 'status' || row.type === 'epic')
                            ? 'bg-purple-50'
                            : row.isSummary
                                ? (isClientTask ? 'bg-blue-50' : 'bg-green-50')
                                : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                        // Detect group boundaries for Status and Main Task groups
                        const nextRow = rows[rowIdx + 1];

                        // Status/Epic group detection
                        const isInStatusGroup = row.type === 'status' || row.type === 'epic' || (row.parentStatusId !== undefined) || (row.parentEpicId !== undefined);
                        const isStatusGroupEnd = isInStatusGroup && (!nextRow || nextRow.type === 'status' || nextRow.type === 'epic' || (nextRow.type === 'task' && !nextRow.parentStatusId && !nextRow.parentEpicId));

                        // Main task group detection (task with subtasks)
                        const isMainTaskGroupEnd = row.type === 'subtask' && (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.mainTaskId);

                        // Row border styles - top/bottom borders for group boundaries
                        // Client (Team) tasks use blue, others use green
                        let rowBorderClass = 'border-b border-gray-100';

                        // Determine if this subtask's parent is a client task
                        const parentTask = row.type === 'subtask' ? rows.find(r => r.type === 'task' && r.originalId === row.mainTaskId && r.projectId === row.projectId) : null;
                        const isClientSubtask = parentTask && teamSwimlaneId && parentTask.swimlaneId === teamSwimlaneId;
                        const useBlue = isClientTask || isClientSubtask;

                        if (row.type === 'project') {
                            // Project row - no extra borders
                            rowBorderClass = 'border-b border-gray-100';
                        } else if (row.type === 'status' || row.type === 'epic') {
                            // Status/Epic row - purple top border, bottom if group ends
                            rowBorderClass = 'border-t border-t-purple-300 border-b border-b-gray-100';
                            if (isStatusGroupEnd) rowBorderClass = 'border-t border-t-purple-300 border-b border-b-purple-300';
                        } else if (row.type === 'task' && row.isSummary) {
                            // Main task row - blue for client tasks, green for others (add-ins use left accent only)
                            if (useBlue) {
                                rowBorderClass = 'border-t border-t-blue-400 border-b border-b-gray-100';
                                if (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.originalId) {
                                    rowBorderClass = 'border-t border-t-blue-400 border-b border-b-blue-400';
                                }
                            } else {
                                rowBorderClass = 'border-t border-t-green-300 border-b border-b-gray-100';
                                if (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.originalId) {
                                    rowBorderClass = 'border-t border-t-green-300 border-b border-b-green-300';
                                }
                            }
                        } else if (row.type === 'subtask' && isMainTaskGroupEnd) {
                            // Last subtask - blue for client subtasks, green for others
                            rowBorderClass = useBlue ? 'border-b border-b-blue-400' : 'border-b border-b-green-300';
                        } else if (isInStatusGroup && isStatusGroupEnd) {
                            // Last row in status group - purple bottom border
                            rowBorderClass = 'border-b border-b-purple-300';
                        }

                        // Left border accent for client (Team) tasks
                        const isTeamTask = row.type === 'task' && teamSwimlaneId && row.swimlaneId === teamSwimlaneId;

                        // Add-in row background colour
                        const rowStyle = { height: rowHeight };
                        if (row._isAddin && row._addinColour && (row.type === 'task' || row.type === 'subtask')) {
                            rowStyle.backgroundColor = row._addinColour + '20'; // 20 = ~12% opacity for pale tint
                        }

                        // Project Overview stays on screen. ALWAYS, not only while
                        // following (Bill, 2026-08-24) - the row saying how the whole
                        // project is doing is the one worth keeping in front of a
                        // reader whatever they are scrolled to. It began as a follow
                        // mode measure (2026-08-23), where scrolling the anchor's
                        // group header to the top takes row 0 with it; nothing about
                        // that reasoning was ever specific to follow mode.
                        //
                        // Everything the pin needs was already unconditional: the
                        // z-30 and the top offset below, and bg-slate-300 above, which
                        // is opaque so rows travelling beneath do not show through.
                        // Only the condition was gated.
                        //
                        // Sticky UNDER the date header, never over it - the header is
                        // z-40 as of 2026-08-24, so that now holds by stacking order
                        // and not only by the top offset. Same fallback header height
                        // the grid and arrow layers use.
                        //
                        // TemplateGantt needs no equivalent: templates have no project
                        // row at all.
                        const pinnedRow = row.type === 'project';

                        return (
                            <div
                                key={row.id}
                                className={`flex ${rowBorderClass} ${rowBgClass}${pinnedRow ? ' sticky z-30' : ''}`}
                                style={pinnedRow
                                    ? { ...rowStyle, top: headerHeight || (currentScale !== 'month' ? 64 : 36) }
                                    : rowStyle}
                            >
                                {/* Left panel cells */}
                                <div
                                    className="sticky left-0 flex z-20 border-r border-gray-400"
                                    style={{
                                        boxShadow: isTeamTask ? 'inset 3px 0 0 0 #3b82f6' : 'none',
                                        background: row._isAddin && row._addinColour && (row.type === 'task' || row.type === 'subtask')
                                            ? `linear-gradient(${row._addinColour}20, ${row._addinColour}20), ${row.isSummary ? '#f0fdf4' : '#ffffff'}`
                                            : 'inherit'
                                    }}
                                >
                                    {/* Task Name with expand/collapse */}
                                    <div
                                        className={`flex items-center px-2 border-r border-gray-400 overflow-hidden ${
                                            reorderDrag?.targetRowId === row.id ? 'bg-blue-100' : ''
                                        }`}
                                        style={{ width: effNameColWidth }}
                                        onDragOver={(e) => {
                                            // Allow drop if same orderKey (for statuses, tasks) or same mainTaskId (for subtasks)
                                            const canDrop = !readOnly && reorderDrag && (
                                                (row.orderKey && row.orderKey === reorderDrag.orderKey) ||
                                                (row.type === 'subtask' && row.mainTaskId && row.mainTaskId.toString() === reorderDrag.orderKey)
                                            );
                                            if (canDrop) {
                                                e.preventDefault();
                                                setReorderDrag(prev => ({ ...prev, targetRowId: row.id, targetOriginalId: row.originalId }));
                                            }
                                        }}
                                    >
                                        {!nameColCollapsed && <div style={{ width: indentPx }} className="flex-shrink-0" />}

                                        {/* Drag handle for reordering (admin only) - statuses, tasks, and subtasks.
                                            The grip is exactly the 20px strip, so it SURVIVES a folded name
                                            column - reorder keeps working collapsed (organise T162 rule).

                                            ⛔ AND IT GOES BELOW THE TEXT THRESHOLD (D5, T398). It is a
                                            fixed 20px that does not scale, and the row is 11px tall at
                                            30% - it would overflow into its neighbours. The else
                                            branch's spacer renders instead, so the indent is unchanged
                                            and the panel does not reflow. */}
                                        {showText && !readOnly && !viewMode && (
                                            (onSubtaskOrderChange && (row.orderKey || row.mainTaskId)) ||
                                            (onMainTaskOrderChange && row.type === 'task' && row.orderKey)
                                        ) ? (
                                            // `fixed`: the name-column cell is overflow-hidden, which
                                            // clips the default CSS bubble. flex-shrink-0 moves to the
                                            // wrapper so the grip keeps its width in the flex row.
                                            <Tooltip text="Drag to reorder" fixed className="flex-shrink-0">
                                            <div
                                                draggable
                                                onDragStart={(e) => {
                                                    // Use orderKey for statuses/tasks, mainTaskId for subtasks
                                                    const orderKey = row.orderKey || (row.mainTaskId ? row.mainTaskId.toString() : null);
                                                    setReorderDrag({
                                                        sourceRowId: row.id,
                                                        sourceOriginalId: row.originalId,
                                                        orderKey: orderKey,
                                                        projectId: row.projectId
                                                    });
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', ''); // Required for Firefox
                                                }}
                                                onDragEnd={() => {
                                                    // Use stored target from reorderDrag state
                                                    if (reorderDrag?.targetOriginalId) {
                                                        handleRowReorder(reorderDrag);
                                                    } else {
                                                        setReorderDrag(null);
                                                    }
                                                }}
                                                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab"
                                                aria-label="Drag to reorder"
                                            >
                                                ⋮
                                            </div>
                                            </Tooltip>
                                        ) : (
                                            <div className="w-5 flex-shrink-0" />
                                        )}

                                        {!nameColCollapsed && (<>
                                        {/* Expand/collapse triangle. Same treatment as the grip
                                            above below the text threshold, for the same reason. */}
                                        {row.hasChildren && showText ? (
                                            <button
                                                onClick={() => toggleExpand(row.id)}
                                                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 flex-shrink-0"
                                            >
                                                {row.isExpanded ? '▼' : '►'}
                                            </button>
                                        ) : (
                                            <div className="w-5 flex-shrink-0" />
                                        )}

                                        {/* Task name. `fixed` because the cell is overflow-hidden;
                                            min-w-0 flex-1 moves to the wrapper so the button inside
                                            still truncates rather than pushing the row wide.

                                            ⛔ BELOW THE TEXT THRESHOLD THE BUTTON GOES WITH ITS TEXT
                                            (D10): an invisible click target that opens a task is
                                            worse than none, and a hover bubble on 4.5px of nothing
                                            is worse still. The bare div keeps the flex geometry the
                                            Tooltip wrapper was providing, so the cell does not
                                            reflow. The BAR stays clickable at every reading size. */}
                                        {showText ? (
                                        <Tooltip text={row.text} fixed className="min-w-0 flex-1">
                                        <button
                                            onClick={() => row.type !== 'status' && row.type !== 'epic' && row.type !== 'project' && onTaskClick?.(row.originalId)}
                                            className={`text-[calc(15px*var(--gantt-scale))] truncate text-left w-full ${
                                                row.type === 'project'
                                                    ? 'font-bold text-slate-700 cursor-default'
                                                    : (row.type === 'status' || row.type === 'epic')
                                                        ? 'font-semibold text-gray-800 cursor-default'
                                                        : row.isClosed
                                                            ? 'text-gray-400 line-through hover:text-gray-500'
                                                            : 'text-gray-700 hover:text-primary-600'
                                            }`}
                                        >
                                            {row.isClosed && row.type === 'task' && (
                                                <FaCheck className="inline-block text-green-600 mr-1 flex-shrink-0" size={10} />
                                            )}
                                            {row.text}
                                            {row.childCount > 0 && (
                                                <span className="text-xs text-gray-400 ml-1">({row.childCount})</span>
                                            )}
                                        </button>
                                        </Tooltip>
                                        ) : (
                                            <div className="min-w-0 flex-1" />
                                        )}
                                        </>)}
                                    </div>

                                    {datesCollapsed ? (
                                        /* Folded dates: ONE 20px spacer keeps the panel's
                                           flex content matching its width */
                                        <div className="flex-shrink-0" style={{ width: COLLAPSED_COL_WIDTH }} />
                                    ) : (<>
                                    {/* Start Date - clickable for editable rows */}
                                    <div
                                        className={`flex items-center justify-center px-1 text-[calc(15px*var(--gantt-scale))] border-r border-gray-100 ${
                                            !readOnly && !row.isSummary && row.type !== 'status' && row.type !== 'epic'
                                                ? 'cursor-pointer hover:bg-blue-50 text-gray-700'
                                                : 'text-gray-600'
                                        }`}
                                        style={{ width: startDateColWidth }}
                                        onClick={() => {
                                            // D10: no invisible editor. Below the threshold the cell
                                            // renders nothing, so opening one would put an input on a
                                            // 4.5px row with no visible label beside it.
                                            if (!showText) return;
                                            if (!readOnly && !row.isSummary && row.type !== 'status' && row.type !== 'epic') {
                                                setEditingDateRow(row.id);
                                            }
                                        }}
                                    >
                                        {showText && (editingDateRow === row.id ? (
                                            <input
                                                type="date"
                                                className="w-full text-[calc(15px*var(--gantt-scale))] border border-blue-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                defaultValue={row.start ? formatDateYMD(row.start) : ''}
                                                autoFocus
                                                onBlur={(e) => {
                                                    const newValue = e.target.value;
                                                    setEditingDateRow(null);
                                                    // Don't update if dependency drag is active
                                                    if (dependencyDragRef.current) {
                                                        return;
                                                    }
                                                    const originalStartFormatted = row.start ? formatDateYMD(row.start) : '';
                                                    if (newValue && newValue !== originalStartFormatted) {
                                                        // FS clamp: a typed start stops at the
                                                        // dependency boundary (Bill, 2026-07-29).
                                                        // Never silent, and never capped at this
                                                        // task's own end - an end before a start is
                                                        // refused (Bill, 2026-08-03). The bound is
                                                        // read off the edges rather than via
                                                        // clampStart, which caps at task.end and so
                                                        // would hide that case.
                                                        let commitStart = newValue;
                                                        if (row.type === 'subtask') {
                                                            const { ctx, rowByKey } = buildFsCtx();
                                                            const key = `${row.ref}|${row.projectId || ''}`;
                                                            const self = ctx.tasksByKey[key];
                                                            if (self) {
                                                                let lo = -Infinity;
                                                                let loBy = null;
                                                                for (const edge of ctx.edges) {
                                                                    if (edge.target !== key) continue;
                                                                    const src = ctx.tasksByKey[edge.source];
                                                                    if (src && src.end != null && src.end + 1 > lo) {
                                                                        lo = src.end + 1;
                                                                        loBy = rowByKey[edge.source]?.text;
                                                                    }
                                                                }
                                                                const typedDay = toDayNum(newValue);
                                                                if (typedDay > self.end) {
                                                                    alertEndBeforeStart({
                                                                        start: newValue,
                                                                        end: fromDayNum(self.end),
                                                                        typed: null,
                                                                        clampedBy: null
                                                                    });
                                                                    return;
                                                                }
                                                                const clampedDay = Math.max(typedDay, lo);
                                                                if (clampedDay > self.end) {
                                                                    alertEndBeforeStart({
                                                                        start: fromDayNum(clampedDay),
                                                                        end: fromDayNum(self.end),
                                                                        typed: newValue,
                                                                        clampedBy: loBy
                                                                    });
                                                                    return;
                                                                }
                                                                if (clampedDay !== typedDay) {
                                                                    const accepted = confirmClampAdjustments([{
                                                                        field: 'start',
                                                                        from: newValue,
                                                                        to: fromDayNum(clampedDay),
                                                                        boundBy: loBy
                                                                    }]);
                                                                    if (!accepted) return;
                                                                }
                                                                commitStart = fromDayNum(clampedDay);
                                                            }
                                                        }
                                                        // Project window warning on the clamped value
                                                        const cellWindow = confirmProjectWindow({
                                                            start: commitStart,
                                                            dueDate: null,
                                                            kickoffDate: kickOffDate,
                                                            label: row.text || 'This task'
                                                        });
                                                        if (!cellWindow.proceed) return;
                                                        // Enqueue operation (optimistic UI)
                                                        onTaskUpdate?.(row.originalId, {
                                                            start: commitStart,
                                                            end: row.end || commitStart,
                                                            isSubtask: row.type === 'subtask',
                                                            allowBeforeKickoff: cellWindow.override
                                                        });
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.target.blur();
                                                    } else if (e.key === 'Escape') {
                                                        setEditingDateRow(null);
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span className={!row.start && !readOnly && !row.isSummary ? 'text-gray-400 italic' : ''}>
                                                {row.start ? formatDateUK(row.start) : (readOnly || row.isSummary ? '-' : 'Click to set')}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Duration. The cell and its width stay below the text
                                        threshold - only the contents go, so the panel is the same
                                        width at 30% as at 100% and nothing reflows. */}
                                    <div
                                        className="flex items-center justify-center px-2 text-[calc(15px*var(--gantt-scale))] text-gray-600"
                                        style={{ width: durationColWidth }}
                                    >
                                        {showText && (calculateDuration(row.start, row.end) || '-')}
                                    </div>
                                    </>)}
                                {/* No effort cell - see the header's ⛔ note:
                                    the left-panel column was vetoed. */}
                                </div>

                                {/* Timeline bar area */}
                                <div
                                    data-gantt-pan
                                    className="relative flex-shrink-0 overflow-hidden"
                                    style={{ width: timelineWidth, cursor: 'grab' }}
                                >
                                    {/* Grid is drawn ONCE for the whole chart, behind
                                        every row - see the grid layer above the rows.
                                        The project row keeps its darker weekend shading,
                                        which the shared layer cannot express, so that
                                        one row overlays its own weekend bands. */}
                                    {row.type === 'project' && (
                                        <div className="absolute inset-0 pointer-events-none">
                                            {columns.map((col, cIdx) => (col.isWeekend ? (
                                                <div
                                                    key={cIdx}
                                                    className="absolute top-0 bottom-0 bg-slate-400/60"
                                                    style={{ left: cIdx * columnWidth, width: columnWidth }}
                                                />
                                            ) : null))}
                                        </div>
                                    )}

                                    {/* Task bar */}
                                    {pos && (() => {
                                        // For subtasks, calculate segments to show weekend gaps
                                        const segments = row.type === 'subtask' && !row.isSummary
                                            ? calculateBarSegments(row, pixelsPerDay, dateRange.minDate, weekendWork)
                                            : [];
                                        const hasWeekendGaps = segments.some(s => s.isWeekendGap);

                                        // Check if subtask is blocked
                                        const blockers = row.type === 'subtask' && row.originalId
                                            ? getBlockers(blockedTasksMap, row.originalId, row.projectId, false)
                                            : [];
                                        const isBlocked = blockers.length > 0;
                                        const hasIssueBlocker = blockers.some(b => b.type === 'issue');

                                        // ⛔ ONE LABEL PER BAR, AND EVERYTHING DOWNSTREAM USES THIS
                                        // SAME STRING (T399) - the bar's own text, the weekend word
                                        // flow, the overflow measurement and the after-bar label.
                                        // Two of the four disagreed until 2026-08-30: the plain bar
                                        // hardcoded 'Meeting' while the segmented path flowed
                                        // row.text, so one meeting renamed itself depending on
                                        // whether a weekend fell inside its dates.
                                        // T471 - the effort suffix, `(Est.4d · Act.6.5d)`.
                                        //
                                        // ⛔ APPENDED HERE, WHERE barLabel IS DEFINED, AND
                                        // NOWHERE ELSE. All four consumers below read this one
                                        // string - the word flow, the overflow measurement, the
                                        // bar's own text and the after-bar label. Appending it at
                                        // a render site instead would leave every measured width
                                        // short by the suffix, so names would wrap or truncate
                                        // wrongly - and only on bars of certain widths, which is
                                        // how the T399 version of this bug survived review.
                                        //
                                        // MAIN-TASK ROWS ONLY (D3). ⚠️ A main task's row type is
                                        // 'task', not 'maintask'.
                                        //
                                        // ⛔ AND NOT THE CLIENT'S LANE (D1, 2026-09-02). Dropped
                                        // HERE, with the rest of the label, because barLabel feeds
                                        // three canvas measurers - a suffix removed at a render
                                        // site would leave every measured width wrong by its
                                        // length, and only bars of certain widths would show it.
                                        //
                                        // ⚠️ TWO ARGUMENTS. This component has no `project` in
                                        // scope and must not gain one; the predicate's third
                                        // parameter is optional and its arm is dead anyway.
                                        const effortSuffix = showEffort && row.type === 'task'
                                            && !isClientLaneTask(row, teamSwimlaneId)
                                            ? effortLabel(row.effortEstimated, row.effortActual)
                                            : null;
                                        const baseLabel = row.isMeeting ? `${row.text} (Meeting)` : row.text;
                                        const barLabel = effortSuffix ? `${baseLabel} ${effortSuffix}` : baseLabel;
                                        const effortOverrun = effortSuffix
                                            && isOverrun(row.effortEstimated, row.effortActual);
                                        // ⚠️ THE RENDERED NODES, whose TEXT IS IDENTICAL to
                                        // barLabel - `baseLabel` + a space + `effortSuffix`. Split
                                        // only so the overrun can be red (D16); a test asserts the
                                        // two agree, because a difference between what is measured
                                        // and what is drawn is exactly the defect this comment
                                        // exists to prevent.
                                        const barLabelNodes = effortOverrun
                                            ? <>{`${baseLabel} `}<span className="text-red-300">{effortSuffix}</span></>
                                            : barLabel;

                                        // ⚠️ HOISTED OUT OF THE SEGMENT MAP (T391). Both were
                                        // computed once per segment, so the whole word flow ran N
                                        // times per bar; the after-bar label needs the flows once,
                                        // before the map. Same inputs, same outputs, one call.
                                        const workingSegs = segments.filter(s => !s.isWeekendGap);
                                        const segmentFlows = hasWeekendGaps
                                            ? flowWords(barLabel, workingSegs.map(w => w.width), nameMeasure)
                                            : [];

                                        // The percentage decides FIRST, then the name (§4.9). It
                                        // does not depend on the name - which truncates to nothing -
                                        // so there is no circularity, but the other order invents
                                        // one.
                                        const percentWidth = row.progress > 0 ? percentMeasure(`${row.progress}%`) : 0;
                                        const percentShown = row.progress !== undefined && row.progress > 0
                                            && !hasWeekendGaps && showText
                                            && percentLabelFits(
                                                Math.max(pos.width, 20),
                                                percentWidth,
                                                hasIssueBlocker ? BLOCKED_ICON_WIDTH : 0
                                            );

                                        // ⛔ RESERVE WHAT ACTUALLY RENDERS, NOT WHAT MIGHT. The
                                        // percentage is absent on a zero-progress bar and on one the
                                        // check above has just hidden; the icon is absent unless it
                                        // is drawn. Reserve either unconditionally and a bar with
                                        // neither is judged ~50px narrower than it is, so a name
                                        // that fits sprouts a duplicate beside it.
                                        const nameReserved = (percentShown ? percentWidth + 10 : 0)
                                            + (hasIssueBlocker && showText ? BLOCKED_ICON_WIDTH : 0);

                                        // A weekend-gapped bar is NOT measured: flowWords already
                                        // decides, setting truncate on the piece it could not fit.
                                        const nameOverflows = hasWeekendGaps
                                            ? segmentFlows.some(f => f.truncate)
                                            : barNameOverflows(
                                                Math.max(pos.width, 20),
                                                barNameMeasure(barLabel),
                                                nameReserved
                                            );

                                        return (
                                        <>

                                        <div
                                            className={`absolute rounded flex items-center z-10 ${
                                                isDragging ? 'opacity-70 ring-2 ring-primary-500' : ''
                                            } ${
                                                pos.isPushed ? 'opacity-80 ring-2 ring-orange-400' : ''
                                            } ${
                                                dependencyDrag && row.type === 'subtask' && row.id !== dependencyDrag.sourceRowId
                                                    ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
                                            } ${
                                                // Every bar is h-7 (Bill, 2026-08-23). Summary rows -
                                                // epics, statuses and a main task WITH subtasks - used to
                                                // be h-5, which made main-task height inconsistent with
                                                // itself: a childless main task took the else branch and
                                                // rendered full height. The thin bar was also why the
                                                // label looked cramped.
                                                row.type === 'project' ? 'justify-center' : ''
                                            } ${
                                                // h-7 as a scaled class, so the bar grows with the
                                                // row around it (T372). 1.75rem is exactly what h-7
                                                // was. A CLASS rather than an inline style because
                                                // jsdom's CSS parser drops calc() containing var(),
                                                // so an inline height would be untestable here -
                                                // and untested geometry on this chart is how the
                                                // last two layout defects shipped.
                                                'h-[calc(1.75rem*var(--gantt-scale))]'
                                            }`}
                                            style={{
                                                // Dependency-chain dimming, matching the 0.25 the
                                                // lines use. Bars only - row backgrounds and the
                                                // left panel stay legible. undefined when no chain
                                                // is hovered, so the drag/pushed opacity classes
                                                // above are never overridden.
                                                opacity: chainRowIds && !chainRowIds.has(row.id)
                                                    ? 0.25
                                                    : undefined,
                                                left: pos.left,
                                                width: pos.width,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: hasWeekendGaps ? 'transparent' : getBarGradient(barColour),
                                                // Segmented bars are transparent here - their segments carry the
                                                // shadow, or it would draw across the weekend gaps.
                                                // Was '2px 3px 6px rgba(0,0,0,0.4)' - a wide soft smudge. Same geometry as
                                                // organise-app's T163 button shadow (0 1px 2px), a step darker: gray-600 at
                                                // 0.75. Inline because it always was; a class loses to an inline style.
                                                ...(hasWeekendGaps ? {} : { boxShadow: '0 1px 2px rgba(75, 85, 99, 0.75)' }),
                                                minWidth: 20,
                                                cursor: readOnly ? 'default' :
                                                    (row.type === 'status' || row.type === 'epic') ? 'default' :
                                                    (dependencyDrag && row.type === 'subtask' ? 'crosshair' : 'grab'),
                                                ...(isNeedsInfo(row.status) && !row.isClosed ? { animation: 'needsInfoPulse 2s ease-in-out infinite' } : {})
                                            }}
                                            onPointerDown={(e) => {
                                                // Ctrl+click on subtask
                                                if (e.ctrlKey && row.type === 'subtask' && !readOnly) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const rect = e.currentTarget.getBoundingClientRect();

                                                    // Only check for weekend click on day scale
                                                    if (currentScale === 'day' && onWeekendWorkChange) {
                                                        const clickX = e.clientX;
                                                        const clickedDate = getClickedDate(
                                                            clickX,
                                                            rect.left,
                                                            rect.width,
                                                            row.start,
                                                            row.end
                                                        );

                                                        if (clickedDate && isWeekend(clickedDate)) {
                                                            // Show weekend work popup
                                                            setWeekendPopup({
                                                                row,
                                                                weekendDate: clickedDate,
                                                                x: e.clientX,
                                                                y: rect.bottom + 5
                                                            });
                                                            return;
                                                        }
                                                    }

                                                    // Otherwise show status popup
                                                    if (onStatusChange) {
                                                        // Check if blocked
                                                        const taskBlockers = getBlockers(blockedTasksMap, row.originalId, row.projectId, false);
                                                        setStatusPopup({
                                                            row,
                                                            x: rect.left,
                                                            y: rect.bottom + 5,
                                                            blockers: taskBlockers
                                                        });
                                                    }
                                                    return;
                                                }
                                                if (e.shiftKey && row.type === 'subtask') {
                                                    handleDependencyDragStart(e, row);
                                                } else if (!readOnly && row.type !== 'status') {
                                                    handleDragStart(e, row);
                                                }
                                                handleBarTouchStart(e, row);
                                            }}
                                            onPointerMove={handleBarTouchMove}
                                            onPointerUp={(e) => handleBarTouchEnd(e, row)}
                                            onPointerCancel={() => {
                                                // ⛔ pointercancel IS NOT pointerup on a bar. The
                                                // release path ends in a TAP, so a gesture iOS
                                                // steals - the left-edge back swipe, a system
                                                // sheet, an incoming call - would otherwise pop a
                                                // card over a chart the user is no longer touching.
                                                clearHold();
                                                touchBarRef.current = null;
                                            }}
                                            onMouseUp={(e) => {
                                                if (dependencyDrag) handleDependencyDrop(e, row);
                                            }}
                                            onMouseEnter={(e) => handleBarMouseEnter(e, row)}
                                            onMouseLeave={handleBarMouseLeave}
                                            onContextMenu={(e) => {
                                                // Right-click on subtask - show assignee popup
                                                if (row.type === 'subtask' && !readOnly && onAssigneeChange) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setAssigneePopup({
                                                        row,
                                                        x: e.clientX,
                                                        y: e.clientY + 5
                                                    });
                                                }
                                            }}
                                        >
                                            {/* Render segments for subtasks with weekend gaps */}
                                            {hasWeekendGaps && segments.map((seg, segIdx) => {
                                                return seg.isWeekendGap ? (
                                                    // Weekend gap - arrow pointing right
                                                    <svg
                                                        key={segIdx}
                                                        className="absolute pointer-events-none"
                                                        style={{
                                                            left: seg.left - pos.left,
                                                            width: seg.width,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            height: '12px',
                                                            overflow: 'visible'
                                                        }}
                                                        viewBox={`0 0 ${seg.width} 12`}
                                                        preserveAspectRatio="none"
                                                    >
                                                        {/* Arrow line - dashed */}
                                                        <line
                                                            x1="0"
                                                            y1="6"
                                                            x2={seg.width - 6}
                                                            y2="6"
                                                            stroke="#9ca3af"
                                                            strokeWidth="2"
                                                            strokeDasharray="4 3"
                                                        />
                                                        {/* Arrow head */}
                                                        <polygon
                                                            points={`${seg.width - 9},1.5 ${seg.width},6 ${seg.width - 9},10.5`}
                                                            fill="#9ca3af"
                                                        />
                                                    </svg>
                                                ) : (
                                                    // Regular bar segment
                                                    (() => {
                                                        // Calculate progress fill for this segment
                                                        // Get total working width and cumulative width up to this segment
                                                        // (workingSegs and the flow are hoisted above the map - T391)
                                                        const totalWorkingWidth = workingSegs.reduce((sum, s) => sum + s.width, 0);
                                                        const segWorkingIndex = workingSegs.indexOf(seg);
                                                        const cumulativeWidth = workingSegs.slice(0, segWorkingIndex).reduce((sum, s) => sum + s.width, 0);
                                                        const flow = segmentFlows[segWorkingIndex]
                                                            || { text: '', startChevron: false, endChevron: false, truncate: false };
                                                        const progressWidth = (row.progress / 100) * totalWorkingWidth;

                                                        // How much progress fills this segment
                                                        let segProgressWidth = 0;
                                                        if (progressWidth > cumulativeWidth) {
                                                            segProgressWidth = Math.min(progressWidth - cumulativeWidth, seg.width);
                                                        }
                                                        const segProgressPercent = seg.width > 0 ? (segProgressWidth / seg.width) * 100 : 0;

                                                        return (
                                                            <div
                                                                key={segIdx}
                                                                className="absolute pointer-events-none rounded flex items-center overflow-hidden"
                                                                style={{
                                                                    left: seg.left - pos.left,
                                                                    width: seg.width,
                                                                    top: 0,
                                                                    bottom: 0,
                                                                    background: getBarGradient(barColour),
                                                                    boxShadow: '0 1px 2px rgba(75, 85, 99, 0.75)',
                                                                    ...(isNeedsInfo(row.status) && !row.isClosed ? { animation: 'needsInfoPulse 2s ease-in-out infinite' } : {})
                                                                }}
                                                            >
                                                                {/* Progress fill for this segment */}
                                                                {segProgressPercent > 0 && (
                                                                    <div
                                                                        className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                                                                        style={{
                                                                            width: `${segProgressPercent}%`,
                                                                            backgroundColor: getProgressWash(row.type)
                                                                        }}
                                                                    />
                                                                )}
                                                                {/* Whole-word name flow with › continuation chevrons - see
                                                                    gantt/nameFlow.js and GANTT-NAME-FLOW-2026-08-21.md */}
                                                                {/* Double glow, ported from organise-app 2026-08-23. Its L058
                                                                    warns that a shadow tuned for one palette is not tuned for
                                                                    another - this is the reverse port of the value that lesson
                                                                    rejected, taken deliberately (Bill, 2026-08-23). Revert this
                                                                    part alone if it smudges. */}
                                                                {flow.startChevron && showText && (
                                                                    <span className="relative z-10 flex-shrink-0 text-lg leading-none font-bold text-white pl-1 pointer-events-none"
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        ›
                                                                    </span>
                                                                )}
                                                                {flow.text && showText && (
                                                                    <span className={`relative z-10 min-w-0 text-[calc(18px*var(--gantt-scale))] font-semibold text-white pointer-events-none whitespace-nowrap ${flow.startChevron ? 'pl-1' : 'pl-3'} ${(flow.truncate || segWorkingIndex === workingSegs.length - 1) ? 'truncate' : ''} ${row.isClosed ? 'opacity-75' : ''}`}
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        {row.isClosed ? <span className="bar-strike">{flow.text}</span> : flow.text}
                                                                    </span>
                                                                )}
                                                                {flow.endChevron && showText && (
                                                                    <span className="relative z-10 flex-shrink-0 text-lg leading-none font-bold text-white pointer-events-none ml-auto pl-1 pr-1"
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        ›
                                                                    </span>
                                                                )}
                                                {/* Progress percentage on last working segment.
                                                    ⛔ The box here is the SEGMENT, not the bar - and the
                                                    blocked icon is NOT reserved, because segments are
                                                    absolute while the icon sits in the bar's own flex
                                                    flow, so the two never compete for the same pixels. */}
                                                                {segWorkingIndex === workingSegs.length - 1 && row.progress > 0 && showText
                                                                    && percentLabelFits(seg.width, percentMeasure(`${row.progress}%`), 0) && (
                                                                    <span
                                                                        className={`relative z-10 text-[calc(18px*var(--gantt-scale))] text-white/90 pointer-events-none whitespace-nowrap pr-2.5 ml-auto ${row.isClosed ? 'opacity-75' : ''}`}
                                                                        style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                                    >
                                                                        {row.isClosed ? <span className="bar-strike">{row.progress}%</span> : <>{row.progress}%</>}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                );
                                            })}

                                            {/* Left resize handle - only for draggable bars. The
                                                POSITIONING moves to the tooltip wrapper, as an inline
                                                style rather than classes - see Tooltip's `style` note. */}
                                            {!readOnly && !row.isSummary && !dependencyDrag && (
                                                <Tooltip
                                                    text="Drag to change start date"
                                                    fixed
                                                    position="top"
                                                    style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8 }}
                                                >
                                                    <div className="w-full h-full bg-white/40 hover:bg-white/70 cursor-ew-resize rounded-l" />
                                                </Tooltip>
                                            )}

                                            {/* Right resize handle - only for draggable bars */}
                                            {!readOnly && !row.isSummary && !dependencyDrag && (
                                                <Tooltip
                                                    text="Drag to change end date"
                                                    fixed
                                                    position="top"
                                                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8 }}
                                                >
                                                    <div className="w-full h-full bg-white/40 hover:bg-white/70 cursor-ew-resize rounded-r" />
                                                </Tooltip>
                                            )}

                                            {/* Blocked indicator - red left bar */}
                                            {isBlocked && (
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none z-20"
                                                    style={{ backgroundColor: '#dc2626', width: '3px' }}
                                                />
                                            )}

                                            {/* Progress fill - only for non-segmented bars */}
                                            {row.progress > 0 && !hasWeekendGaps && (
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                                                    style={{
                                                        width: `${row.progress}%`,
                                                        backgroundColor: getProgressWash(row.type)
                                                    }}
                                                />
                                            )}

                                            {/* Weekend markers within bar - subtasks only */}
                                            {row.type === 'subtask' && row.start && row.end && currentScale === 'day' && (() => {
                                                const start = new Date(row.start);
                                                const end = new Date(row.end);
                                                const totalDays = Math.max((end - start) / (1000 * 60 * 60 * 24) + 1, 1);
                                                const markers = [];
                                                let current = new Date(start);
                                                current.setUTCDate(current.getUTCDate() + 1); // Start from day after start
                                                while (current <= end) {
                                                    if (current.getUTCDay() === 0 || current.getUTCDay() === 6) {
                                                        const dayOffset = (current - start) / (1000 * 60 * 60 * 24);
                                                        const leftPercent = (dayOffset / totalDays) * 100;
                                                        markers.push(
                                                            <div
                                                                key={current.toISOString()}
                                                                className="absolute top-0 bottom-0 pointer-events-none"
                                                                style={{
                                                                    left: `${leftPercent}%`,
                                                                    width: '1px',
                                                                    backgroundColor: 'rgba(255,255,255,0.2)'
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    current.setUTCDate(current.getUTCDate() + 1);
                                                }
                                                return markers;
                                            })()}

                                            {/* Note: Spinners removed - using optimistic UI now */}

                                            {/* Blocked indicator icon - only for Issue-blocked subtasks.
                                                Hidden below the text threshold (D11): a fixed 12px icon
                                                inside an 8px bar overflows it. Blocking is still
                                                signalled - the 3px red strip above is inset-y, so it
                                                scales with the bar and stays at every reading size. */}
                                            {hasIssueBlocker && showText && (
                                                <Tooltip
                                                    fixed
                                                    position="top"
                                                    className="ml-1 flex-shrink-0 z-10"
                                                    text={`Blocked by: ${blockers.filter(b => b.type === 'issue').map(b => `Issue ${b.id}: ${b.title}`).join(', ')}`}
                                                >
                                                    <FaExclamationTriangle className="text-white" size={12} />
                                                </Tooltip>
                                            )}
                                            {/* Text on bar - show for bars without weekend gaps (segmented bars have text in first segment) */}
                                            {!hasWeekendGaps && showText && (
                                                <span
                                                    className={`relative z-10 pl-3 pr-1 truncate pointer-events-none flex-1 min-w-0 text-[calc(18px*var(--gantt-scale))] font-semibold ${row.isMeeting ? 'text-gray-700' : 'text-white'} ${row.isClosed ? 'opacity-75' : ''}`}
                                                    style={row.isMeeting ? undefined : { textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                >
                                                    {row.isClosed && row.type === 'task' && (
                                                        <FaCheck className="inline-block text-green-400 mr-1 flex-shrink-0" size={10} />
                                                    )}
                                                    {row.isClosed
                                                        ? <span className="bar-strike">{barLabelNodes}</span>
                                                        : barLabelNodes}
                                                </span>
                                            )}
                                            {/* Progress percentage - right side. The fit was decided
                                                above, before the name's own check, because it does not
                                                depend on the name - which truncates to nothing - while
                                                the name's check depends on this one. */}
                                            {percentShown && (
                                                <span
                                                    className={`z-10 text-[calc(18px*var(--gantt-scale))] text-white/90 pointer-events-none whitespace-nowrap pr-2.5 flex-shrink-0 ${row.isClosed ? 'opacity-75' : ''}`}
                                                    style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                >
                                                    {row.isClosed ? <span className="bar-strike">{row.progress}%</span> : <>{row.progress}%</>}
                                                </span>
                                            )}
                                        </div>

                                        {/* The full name, in italics, OUTSIDE the bar and to its
                                            right - and ONLY when the name inside had to be cut
                                            (Bill, 2026-08-30). Style ported verbatim from
                                            organise-app's GanttRow.js (376, 385, 1042-1062).

                                            Two of its rules are behavioural, not cosmetic:
                                            pointer-events-none so the label never eats a drag or a
                                            ctrl+click, and z-[5] - UNDER the bars at z-10 - so a
                                            name that runs on never paints over the next bar. The
                                            16px offset clears the resize grip, which is 8px and sits
                                            on the bar's own right edge: without it the first italic
                                            letter's slant lands on the handle.

                                            The row's timeline cell is overflow-hidden, so a name
                                            running past the chart's right edge is clipped there and
                                            adds no scroll width. */}
                                        {nameOverflows && showText && (
                                            <span
                                                className={`pointer-events-none absolute z-[5] ${AFTER_BAR_NAME_CLASSES}`}
                                                style={{
                                                    left: pos.left + pos.width + AFTER_BAR_NAME_OFFSET,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    textShadow: AFTER_BAR_NAME_GLOW
                                                }}
                                            >
                                                {row.isClosed
                                                    ? <span className="bar-strike bar-strike-thin">{barLabelNodes}</span>
                                                    : barLabelNodes}
                                            </span>
                                        )}
                                        </>
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })}

                    {/* Today - a translucent band the width of the day, not a
                        hairline. As a 2px orange line it was indistinguishable
                        from an orange dependency link, and the 12 dependency
                        colours plus the bar palette leave no free hue to move
                        it to. A filled column is a shape no link can take, so
                        it stops competing on colour altogether.

                        ⛔ zIndex 1 - THE SAME NUMBER AS THE GRID, and it must
                        stay the same number. The grid layer carries an explicit
                        zIndex: 1 (:2797) and an explicit index beats `auto`
                        whatever the DOM order, so without one of its own this
                        band painted UNDER the whole grid despite being the later
                        sibling - under the weekend fill, the column borders and
                        the dashed month rules. Equal indexes fall back to DOM
                        order, and the band is later: above the grid, still below
                        the bars (z-10) and the arrows.

                        The comment here used to claim it "sits above the grid".
                        It did not, and the wrong comment is why this survived.

                        ⚠️ TME's symptom was DIMMING, not disappearance - the
                        weekend fill is bg-gray-200/50 (:2807), translucent. In
                        organise, where the equivalent fill is opaque, the band
                        vanished across every Saturday and Sunday (ISS-087).

                        Floored at 2px so it survives zooming far out, where a
                        day is sub-pixel. */}
                    {todayPosition >= 0 && todayPosition <= timelineWidth && (
                        <div
                            className="absolute pointer-events-none"
                            style={{
                                left: leftPanelWidth + todayPosition,
                                width: Math.max(pixelsPerDay, 2),
                                top: headerHeight || (currentScale !== 'month' ? 64 : 36),
                                height: rows.length * rowHeight,
                                backgroundColor: 'rgba(249, 115, 22, 0.12)',
                                zIndex: 1
                            }}
                        />
                    )}

                    {/* Kick-off line - matches project bar colour */}
                    {kickOffPosition !== null && kickOffPosition >= 0 && kickOffPosition <= timelineWidth && (
                        <div
                            className="absolute w-px pointer-events-none"
                            style={{
                                left: leftPanelWidth + (kickOffPreviewPosition !== null ? kickOffPreviewPosition : kickOffPosition),
                                top: headerHeight || (currentScale !== 'month' ? 64 : 36),
                                height: rows.length * rowHeight,
                                backgroundColor: PROJECT_COLOUR,
                                opacity: kickOffDragging ? 0.7 : 1
                            }}
                        />
                    )}

                    {/* ⚠️ ONE COPY OF THE MARKER'S LABEL. The hover tooltip and the
                        tap bubble (T409) must say the same thing - a finger being
                        told something different from a mouse is how a control ends
                        up with two explanations. */}
                    {/* Kick-off marker chevron in header - draggable.
                        The marker's own placement moves to the tooltip wrapper so the
                        bubble anchors to the chevron. Two lines, so the text is JSX with
                        a <br/> - the native title's \n would have rendered as a space
                        inside the bubble, which is whitespace-nowrap. */}
                    {kickOffPosition !== null && kickOffPosition >= 0 && kickOffPosition <= timelineWidth && !readOnly && (onKickOffDateChange || onProjectDateShift) && (
                        <Tooltip
                            fixed
                            text={kickOffMarkerLabel}
                            style={{
                                position: 'absolute',
                                zIndex: 40,
                                left: leftPanelWidth + (kickOffPreviewPosition !== null ? kickOffPreviewPosition : kickOffPosition) - 8,
                                top: currentScale !== 'month' ? 40 : 12,
                                width: 16,
                                height: 16
                            }}
                        >
                        <div
                            className={`w-full h-full cursor-ew-resize ${kickOffDragging ? 'opacity-70' : 'hover:opacity-80'}`}
                            onPointerDown={(e) => {
                                // ⛔ A FINGER MAY NOT DRAG THIS AT ALL (T409/D12).
                                // Bill, 2026-08-30: "This should not be finger
                                // grabbable its way too important". It moves the
                                // WHOLE project, so unlike a bar it does not get a
                                // confirmation - it gets refused. Mouse and pen are
                                // untouched, answered per EVENT exactly as the grips
                                // are, so an iPad with a trackpad can still do it
                                // from the trackpad in the same session.
                                //
                                // ⚠️ A tap shows the marker's label instead, so the
                                // control explains itself rather than reading as
                                // dead. Returning BEFORE preventDefault is deliberate
                                // - nothing here needs the gesture suppressed.
                                if (e.pointerType === 'touch') {
                                    const box = e.currentTarget.getBoundingClientRect();
                                    if (kickOffTapTimerRef.current) clearTimeout(kickOffTapTimerRef.current);
                                    setKickOffTapLabel({ x: box.left + box.width / 2, y: box.bottom + 8 });
                                    kickOffTapTimerRef.current = setTimeout(() => setKickOffTapLabel(null), 2500);
                                    return;
                                }
                                e.preventDefault();
                                setKickOffDragging(true);

                                const startKickOff = effectiveKickOff ? new Date(effectiveKickOff) : null;
                                let previewDate = startKickOff;

                                const handleMouseMove = (moveE) => {
                                    const scrollContainer = scrollContainerRef.current;
                                    if (!scrollContainer || !startKickOff) return;
                                    const rect = scrollContainer.getBoundingClientRect();
                                    const scrollLeft = scrollContainer.scrollLeft;
                                    const x = moveE.clientX - rect.left + scrollLeft - leftPanelWidth;
                                    const dayOffset = Math.round(x / pixelsPerDay);
                                    const newDate = new Date(dateRange.minDate);
                                    newDate.setUTCDate(newDate.getUTCDate() + dayOffset);

                                    // ⛔ SNAP TO WEEKS AND PREVIEW ONLY - THERE IS NO
                                    // OTHER BRANCH ANY MORE (T409/D12, 2026-08-30).
                                    // A second one used to call onKickOffDateChange on
                                    // EVERY pointermove, writing a new project kick-off
                                    // date per pixel with no confirmation at all. It was
                                    // already unreachable - ClientView is the only host
                                    // and passes onProjectDateShift in the same block -
                                    // so it was a loaded gun for the next screen that
                                    // wired the chart up differently. The marker now has
                                    // exactly one behaviour: preview, then ask.
                                    if (!onProjectDateShift) return;
                                    const deltaDays = Math.round((newDate - startKickOff) / (1000 * 60 * 60 * 24));
                                    const weeks = Math.round(deltaDays / 7);
                                    previewDate = new Date(startKickOff);
                                    previewDate.setUTCDate(previewDate.getUTCDate() + (weeks * 7));
                                    // Skip to Monday if landed on weekend
                                    while (isWeekend(previewDate)) {
                                        previewDate.setUTCDate(previewDate.getUTCDate() + 1);
                                    }
                                    // Update preview position for visual feedback
                                    const previewOffset = (previewDate - dateRange.minDate) / (1000 * 60 * 60 * 24);
                                    setKickOffPreviewPosition(previewOffset * pixelsPerDay);
                                };

                                let ended = false;
                                const handleMouseUp = () => {
                                    // ⛔ IDEMPOTENT: pointerup and
                                    // lostpointercapture both fire on an
                                    // ordinary release, and this one carries a
                                    // confirmation dialog - running it twice
                                    // would ask twice.
                                    if (ended) return;
                                    ended = true;
                                    setKickOffDragging(false);
                                    setKickOffPreviewPosition(null);
                                    document.removeEventListener('pointermove', handleMouseMove);
                                    document.removeEventListener('pointerup', handleMouseUp);
                                    document.removeEventListener('pointercancel', handleMouseUp);
                                    document.removeEventListener('lostpointercapture', handleMouseUp);

                                    // For project shift mode, calculate final delta and show confirmation
                                    if (onProjectDateShift && startKickOff && previewDate) {
                                        const deltaDays = Math.round((previewDate - startKickOff) / (1000 * 60 * 60 * 24));
                                        const weeks = Math.round(deltaDays / 7);

                                        if (weeks !== 0) {
                                            // Count subtasks with dates
                                            const subtaskCount = rows.filter(r =>
                                                r.type === 'subtask' && (r.start || r.end)
                                            ).length;

                                            setShiftConfirmation({
                                                deltaDays: weeks * 7,
                                                weeks,
                                                subtaskCount
                                            });
                                        }
                                    }
                                };

                                document.addEventListener('pointermove', handleMouseMove);
                                document.addEventListener('pointerup', handleMouseUp);
                                document.addEventListener('pointercancel', handleMouseUp);
                                document.addEventListener('lostpointercapture', handleMouseUp);
                            }}
                        >
                            {/* Chevron pointing down */}
                            <svg viewBox="0 0 16 16" fill={PROJECT_COLOUR}>
                                <path d="M8 12L2 4h12L8 12z" />
                            </svg>
                        </div>
                        </Tooltip>
                    )}

                    {/* Dependency drag line (shift+drag in progress) */}
                    {dependencyDrag && (
                        <svg
                            className="absolute inset-0 pointer-events-none z-30"
                            style={{ width: leftPanelWidth + timelineWidth, height: rows.length * rowHeight + 80 }}
                        >
                            <line
                                x1={dependencyDrag.startX}
                                y1={dependencyDrag.startY}
                                x2={cursorPos.x}
                                y2={cursorPos.y}
                                stroke="#3b82f6"
                                strokeWidth="2"
                                strokeDasharray="5,5"
                            />
                            <circle
                                cx={cursorPos.x}
                                cy={cursorPos.y}
                                r="6"
                                fill="#3b82f6"
                            />
                        </svg>
                    )}

                    {/* Dependency arrows between subtask rows */}
                    <DependencyArrows
                        effectiveLinks={effectiveLinks}
                        rows={rows}
                        readOnly={readOnly}
                        headerHeight={headerHeight}
                        currentScale={currentScale}
                        getDisplayPosition={getBarPosition}
                        getDateGap={(sourceRow, targetRow) => {
                            const sourceEnd = sourceRow.end ? new Date(sourceRow.end) : null;
                            const targetStart = targetRow.start ? new Date(targetRow.start) : null;
                            return sourceEnd && targetStart ? (targetStart - sourceEnd) / 86400000 : 0;
                        }}
                        onDependencyDelete={onDependencyDelete}
                        onDependencyColorChange={onDependencyColorChange}
                        setColorPopup={setColorPopup}
                        setTopLinkKey={setTopLinkKey}
                        setPendingLinks={setPendingLinks}
                        leftPanelWidth={leftPanelWidth}
                        timelineWidth={timelineWidth}
                        rowHeight={rowHeight}
                        zoomLevel={zoomLevel}
                        onLinkHover={handleLinkHover}
                    />

                    {/* Bottom border line to match header */}
                    <div
                        className="absolute border-b border-gray-300"
                        style={{
                            left: 0,
                            right: 0,
                            top: (headerHeight || (currentScale !== 'month' ? 64 : 36)) + (rows.length * rowHeight)
                        }}
                    />
                </div>
            </div>

            {/* Footer with time slider */}
            <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-300 bg-gray-50 flex-shrink-0">
                <span className="text-[16px] text-gray-600 whitespace-nowrap">
                    {formatDateUK(dateRange.minDate)}
                </span>
                <div className="flex-1 flex items-center gap-2">
                    <DateSlider
                        value={scrollPercent}
                        onScrub={handleSliderScrub}
                        onCommit={handleSliderCommit}
                        formatValue={dateAtScrollPercent}
                        step={sliderStep}
                    />
                    <Tooltip text="Jump to a date in the project range" position="top">
                        <input
                            type="date"
                            value={jumpDate}
                            min={formatDateYMD(dateRange.minDate)}
                            max={formatDateYMD(dateRange.maxDate)}
                            onChange={handleJumpToDate}
                            className="gantt-date-jump px-2 text-[16px] border border-gray-300 rounded bg-white text-gray-700 cursor-pointer"
                            aria-label="Jump to a date in the project range"
                        />
                    </Tooltip>
                </div>
                <span className="text-[16px] text-gray-600 whitespace-nowrap">
                    {formatDateUK(dateRange.maxDate)}
                </span>
            </div>

            {/* Status change popup */}
            <StatusChangePopup
                statusPopup={statusPopup}
                taskStatuses={taskStatuses}
                onStatusChange={onStatusChange}
                onClosureRequired={onClosureRequired}
                onClose={() => setStatusPopup(null)}
            />

            {/* Assignee change popup */}
            <AssigneeChangePopup
                assigneePopup={assigneePopup}
                members={members}
                onAssigneeChange={onAssigneeChange}
                onClose={() => setAssigneePopup(null)}
            />

            {/* Weekend work popup */}
            {weekendPopup && (
                <div
                    className="fixed inset-0 z-50"
                    onClick={() => setWeekendPopup(null)}
                >
                    <div
                        className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]"
                        style={{ left: weekendPopup.x, top: weekendPopup.y }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const subtaskId = String(weekendPopup.row.originalId);
                            // Get both Saturday and Sunday for this weekend
                            const clickedDate = new Date(weekendPopup.weekendDate);
                            const saturday = clickedDate.getUTCDay() === 6
                                ? new Date(clickedDate)
                                : new Date(clickedDate.getTime() - 24 * 60 * 60 * 1000);
                            const sunday = clickedDate.getUTCDay() === 0
                                ? new Date(clickedDate)
                                : new Date(clickedDate.getTime() + 24 * 60 * 60 * 1000);
                            const satKey = formatDateYMD(saturday);
                            const sunKey = formatDateYMD(sunday);
                            const satWorked = weekendWork[subtaskId]?.[satKey] === true;
                            const sunWorked = weekendWork[subtaskId]?.[sunKey] === true;

                            const formatShort = (d) => d.toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' });

                            return (
                                <>
                                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                                        Weekend: {formatShort(saturday)} - {formatShort(sunday)}
                                    </div>
                                    {/* Saturday */}
                                    <button
                                        className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                                            satWorked ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            onWeekendWorkChange(subtaskId, satKey, !satWorked);
                                            setWeekendPopup(null);
                                        }}
                                    >
                                        <span>Saturday</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${satWorked ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                            {satWorked ? 'Worked' : 'Not Worked'}
                                        </span>
                                    </button>
                                    {/* Sunday */}
                                    <button
                                        className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                                            sunWorked ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            onWeekendWorkChange(subtaskId, sunKey, !sunWorked);
                                            setWeekendPopup(null);
                                        }}
                                    >
                                        <span>Sunday</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${sunWorked ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                            {sunWorked ? 'Worked' : 'Not Worked'}
                                        </span>
                                    </button>
                                    {/* Both days */}
                                    <div className="border-t border-gray-100 mt-1 pt-1">
                                        <button
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 text-gray-700"
                                            onClick={() => {
                                                setWeekendPopup(null);
                                                onWeekendWorkChange(subtaskId, [
                                                    { dateKey: satKey, worked: true },
                                                    { dateKey: sunKey, worked: true }
                                                ]);
                                            }}
                                        >
                                            Mark both worked
                                        </button>
                                        <button
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 text-gray-700"
                                            onClick={() => {
                                                setWeekendPopup(null);
                                                onWeekendWorkChange(subtaskId, [
                                                    { dateKey: satKey, worked: false },
                                                    { dateKey: sunKey, worked: false }
                                                ]);
                                            }}
                                        >
                                            Mark both not worked
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Dependency colour picker popup */}
            <DependencyColourPicker
                colorPopup={colorPopup}
                onDependencyColorChange={onDependencyColorChange}
                setTopLinkKey={setTopLinkKey}
                onClose={() => setColorPopup(null)}
            />

            {/* Project Date Shift Confirmation Dialog */}
            {shiftConfirmation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Move Entire Project?
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            This will shift the project by <strong>{Math.abs(shiftConfirmation.weeks)} week{Math.abs(shiftConfirmation.weeks) !== 1 ? 's' : ''}</strong> {shiftConfirmation.weeks > 0 ? 'forward' : 'backward'}.
                        </p>
                        <p className="text-gray-600 mb-6">
                            <strong>{shiftConfirmation.subtaskCount}</strong> subtask{shiftConfirmation.subtaskCount !== 1 ? 's' : ''} will be updated.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                onClick={() => setShiftConfirmation(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                                onClick={() => {
                                    if (onProjectDateShift) {
                                        onProjectDateShift(shiftConfirmation.deltaDays, shiftConfirmation.subtaskCount);
                                    }
                                    setShiftConfirmation(null);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* The question a finger's bar drag asks (T402). Rendered here so it
                sits above the chart; it draws its own backdrop. */}
            <GanttDragConfirm
                confirm={dragConfirm}
                formatDate={formatDateUK}
                countLabel={dragConfirmCountLabel}
                onCancel={() => {
                    // Dismissing IS cancelling. Both previews go, so the bar and
                    // anything it pushed snap back to their stored dates - and
                    // nothing was ever written to undo.
                    setDragConfirm(null);
                    clearDragPreviews();
                }}
                onConfirm={() => {
                    // ⛔ THE PREVIEWS ARE NOT CLEARED HERE - applyDragCommit
                    // ends by clearing them, and it writes the position override
                    // first, which is what holds the bar in its new place until
                    // the props catch up. Clearing them here as well would be
                    // harmless; clearing them INSTEAD would flick the bar back
                    // to its old dates until the save landed.
                    applyDragCommit(dragConfirm);
                    setDragConfirm(null);
                }}
            />

            {/* Project Date Shift Overlay (PROJECT-SHIFT plan §9.2). The shift
                is ONE server call, so there is no per-row progress to report -
                a spinner, not a determinate bar (a fake percentage is worse
                than none). Prop contract: { active: true }. The fixed inset-0
                z-50 backdrop is D8's screen lock - keep it. */}
            {shiftProgress && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4 w-full flex items-center gap-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Shifting project dates…
                        </h3>
                    </div>
                </div>
            )}

            {/* Custom tooltip */}
            {/* What a FINGER gets from the kick-off marker (T409/D12): the same
                label a mouse gets on hover, and no movement. Bill: "This should
                not be finger grabbable its way too important".

                ⚠️ BUBBLE_CLASSES, not a second look. Tooltip exports it precisely
                so another renderer can draw an identical bubble - NativeTooltips
                already does. Restyle there and this follows. */}
            {kickOffTapLabel && (
                <div
                    data-testid="kickoff-tap-label"
                    className={`fixed ${BUBBLE_CLASSES}`}
                    style={{
                        left: kickOffTapLabel.x,
                        top: kickOffTapLabel.y,
                        transform: 'translateX(-50%)',
                        zIndex: 9999
                    }}
                >
                    {kickOffMarkerLabel}
                </div>
            )}
            {navTip && (
                <div
                    className="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg whitespace-nowrap"
                    style={{
                        position: 'fixed',
                        left: navTip.x,
                        top: navTip.y,
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {navTip.text}
                </div>
            )}
            {linkTip && (
                <div
                    className="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-md"
                    style={{
                        position: 'fixed',
                        left: linkTip.x + 18,
                        top: linkTip.y + 18,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {linkTip.content}
                </div>
            )}
            <CustomTooltip
                tooltip={tooltip}
                tooltipPos={tooltipPos}
                tooltipRef={tooltipRef}
                tooltipTimeoutRef={tooltipTimeoutRef}
                setTooltip={setTooltip}
                setTooltipPos={clearTooltipPos}
            />
        </div>
    );
};

export default GanttChart;
